import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
// Removed HighchartsMore/SolidGauge to fix webpack error
import { FiSearch, FiFilter, FiMapPin, FiEdit2, FiCheckCircle, FiXCircle, FiClock, FiChevronLeft, FiChevronRight, FiAlertCircle, FiRefreshCw, FiCalendar, FiFileText, FiChevronUp, FiChevronDown, FiTrendingUp, FiCoffee, FiArrowLeft, FiArrowRight, FiDownload, FiInfo, FiTrash2, FiUsers, FiSmartphone, FiStar } from 'react-icons/fi'; // Icons
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useUser } from '../../contexts/UserContext';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { RiFilePdf2Fill, RiFileExcelFill, RiFileHistoryFill, RiDownload2Fill } from "react-icons/ri";
import ExcelJS from 'exceljs';
import { toJpeg } from 'html-to-image';
import Select from 'react-select';

// Init Chart Modules
// HighchartsMore(Highcharts);
// SolidGauge(Highcharts);

// --- Shift Timing Constants (Fallback Defaults) ---
const DEFAULT_SHIFT_START_MINS = 9 * 60;      // 09:00 AM
const DEFAULT_SHIFT_END_MINS = 18 * 60;       // 06:00 PM
const DEFAULT_SHIFT_START_STR = '09:00';      // Default Shift Start Time
const DEFAULT_LATE_THRESHOLD_STR = '09:05';   // Default Shift Late Cutoff (after 09:05 is late)
const DEFAULT_LATE_THRESHOLD_FULL = '09:06:00'; // Default Shift Late Threshold (including seconds)
const DEFAULT_HALF_DAY_SPLIT_STR = '14:00';   // Default Half-Day Split Time
const DEFAULT_HALF_DAY_SPLIT_FULL = '14:00:00'; // Default Half-Day Split Full
const DEFAULT_SHIFT_END_STR = '18:00';        // Default Shift End Time
const DEFAULT_SHIFT_END_FULL = '18:00:00';    // Default Shift End Full Time

// --- Register View Component (Moved outside to prevent re-mounts) ---
// Local Date Helper to avoid timezone shifts
const toInputDate = (d) => {
    if (!d || isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const cleanStatus = (status) => {
    if (!status) return '';
    let clean = status.split(' (')[0].trim().toUpperCase();
    if (clean === 'WENT_LATE_YESTERDAY') return 'WENT_LATE_YESTERDAY';
    if (clean === 'NOT_MARKED') return 'NOT MARKED';
    return clean;
};

const renderTimeWithIcon = (timeStr) => {
    if (!timeStr || timeStr === '-') return '-';
    return <div className="inline-flex flex-wrap items-center gap-1">
        {timeStr.split(',').map((timePart, idx, arr) => {
            const t = timePart.trim();
            const isMobile = t.includes('(M)');
            const isFace = t.includes('(F)');
            const isGeo = t.includes('(G)');
            const cleanTime = t.replace('(M-IN)', '').replace('(M-OUT)', '').replace('(M)', '')
                .replace('(F-IN)', '').replace('(F-OUT)', '').replace('(F)', '')
                .replace('(G-IN)', '').replace('(G-OUT)', '').replace('(G)', '')
                .trim();
            return (
                <span key={idx} className="inline-flex items-center gap-1">
                    {cleanTime}
                    {isMobile && <FiSmartphone className="text-[12px] text-[#C30010]" title="Mobile App Punch" />}
                    {isFace && <FiCheckCircle className="text-[11px] text-gray-400" title="Virtual Face Punch" />}
                    {isGeo && <FiMapPin className="text-[11px] text-blue-500" title="Geo-Fencing Punch" />}
                    {idx < arr.length - 1 && <span className="mr-0.5">,</span>}
                </span>
            );
        })}
    </div>;
};
const RegisterView = ({ registerDate, setRegisterDate, handleManualClick, handleCancelStatus, refreshTrigger, handlePunchEdit, isHROrAdmin }) => {
    const [localData, setLocalData] = useState([]);
    const [rLoading, setRLoading] = useState(false);
    const [calendarHolidays, setCalendarHolidays] = useState([]);

    // Bulk Actions State
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkStatus, setBulkStatus] = useState('PRESENT');
    const [bulkReason, setBulkReason] = useState('Bulk Action');
    const [bulkLoading, setBulkLoading] = useState(false);
    const [showBulkConfirm, setShowBulkConfirm] = useState(false);

    // Search & Pagination State
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);

    const fetchRegisterData = async () => {
        setRLoading(true);
        setSelectedIds([]); // Clear selection on date change
        try {
            const res = await api.get('/attendance/data', {
                params: { startDate: registerDate, endDate: registerDate }
            });
            if (res.data.status) {
                setLocalData(res.data.data);
            }
        } catch (e) { toast.error('Failed to load register'); }
        finally { setRLoading(false); }
    };

    useEffect(() => {
        fetchRegisterData();
    }, [registerDate, refreshTrigger]);

    // Reset pagination & search ONLY when user explicitly changes date
    useEffect(() => {
        setCurrentPage(1);
        setSearchTerm('');
    }, [registerDate]);

    useEffect(() => {
        const fetchCalendarHolidays = async () => {
            try {
                const year = new Date(registerDate).getFullYear();
                const res = await api.get('/hrms/calendar-events', { params: { year, year_only: 1 } });
                if (res.data.status) {
                    setCalendarHolidays((res.data.data || []).filter(item => item.event_type === 'Holiday'));
                }
            } catch (e) {
                setCalendarHolidays([]);
            }
        };

        if (registerDate) {
            fetchCalendarHolidays();
        }
    }, [registerDate]);

    // Selection Logic
    const handleSelectRow = (userId) => {
        setSelectedIds(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(filteredData.map(d => d.user_id));
        } else {
            setSelectedIds([]);
        }
    };

    const executeBulkAction = async () => {
        if (selectedIds.length === 0) return;
        setShowBulkConfirm(true);
    };

    const confirmBulkAction = async () => {
        setShowBulkConfirm(false);
        setBulkLoading(true);
        try {
            await api.post('/attendance/bulk-update-status', { // Use new endpoint
                user_ids: selectedIds,
                date: registerDate,
                status: bulkStatus,
                reason: bulkReason
            });
            toast.success('Bulk update successful');
            setSelectedIds([]);
            fetchRegisterData();
        } catch (e) {
            toast.error('Bulk update failed');
        } finally {
            setBulkLoading(false);
        }
    };

    // Filter data based on search term
    const filteredData = localData.filter(row => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            row.name?.toLowerCase().includes(search) ||
            row.shift?.toLowerCase().includes(search) ||
            row.status?.toLowerCase().includes(search) ||
            row.punch_records?.toLowerCase().includes(search)
        );
    });

    // Pagination calculations
    const totalEntries = filteredData.length;
    const totalPages = Math.ceil(totalEntries / entriesPerPage);
    const startIndex = (currentPage - 1) * entriesPerPage;
    const endIndex = Math.min(startIndex + entriesPerPage, totalEntries);
    const paginatedData = filteredData.slice(startIndex, endIndex);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, entriesPerPage]);

    // Counts (based on filtered data)
    const countP = filteredData.filter(d => { const s = cleanStatus(d.status); return s === 'PRESENT' || s === 'LATE'; }).length;
    const countA = filteredData.filter(d => { const s = cleanStatus(d.status); return s === 'ABSENT' || s === 'LOP'; }).length;
    const countOD = filteredData.filter(d => cleanStatus(d.status) === 'ON_DUTY').length;
    const countL = filteredData.filter(d => { const s = cleanStatus(d.status); return s === 'ON_LEAVE' || s === 'HALF_DAY'; }).length;
    const countPER = filteredData.filter(d => { const s = cleanStatus(d.status); return s === 'PERMISSION' || s === 'AUTO_PERM'; }).length;
    const registerDateObj = registerDate ? new Date(`${registerDate}T00:00:00`) : null;
    const holidayEvent = calendarHolidays.find(item => item.event_date === registerDate);
    const isBlockedNonWorkingDay = !!holidayEvent;
    const nonWorkingMessage = holidayEvent
        ? `Today is holiday${holidayEvent.event_title ? `: ${holidayEvent.event_title}` : ''}.`
        : '';

    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 animate-fade-in-up">
            {/* Header Controls - All in One Row */}
            <div className="flex flex-col xl:flex-row justify-between items-center mb-6 gap-6">
                {/* Left: Date Picker */}
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full sm:w-auto justify-center sm:justify-start">
                    <div className="flex items-center gap-3 sm:gap-4">
                        <h3 className="font-bold text-lg text-gray-800">Date:</h3>
                        <div className="flex items-center bg-gray-50 rounded-lg p-1 border border-gray-200">
                            <button onClick={() => {
                                const d = new Date(registerDate); d.setDate(d.getDate() - 1);
                                setRegisterDate(d.toISOString().split('T')[0]);
                            }} className="p-1 hover:bg-gray-200 rounded"><FiChevronLeft /></button>
                            <input
                                type="date"
                                value={registerDate}
                                onChange={e => setRegisterDate(e.target.value)}
                                className="bg-transparent border-none text-[13px] sm:text-sm font-bold text-gray-700 focus:ring-0 p-1 w-28 sm:w-auto text-center sm:text-left"
                            />
                            <button onClick={() => {
                                const d = new Date(registerDate); d.setDate(d.getDate() + 1);
                                setRegisterDate(d.toISOString().split('T')[0]);
                            }} className="p-1 hover:bg-gray-200 rounded"><FiChevronRight /></button>
                        </div>
                    </div>
                    {isBlockedNonWorkingDay && (
                        <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs sm:text-sm font-bold text-amber-800 shadow-sm">
                            <FiInfo size={14} />
                            <span>{nonWorkingMessage} Absent and Leave actions are disabled.</span>
                        </div>
                    )}
                </div>

                {/* Center: Status Counts */}
                <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 font-bold text-[11px] sm:text-sm bg-gray-50 p-2 sm:p-3 rounded-xl border border-gray-100">
                    <div className="text-green-600 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500"></span> P: {countP}</div>
                    <div className="text-red-600 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span> A: {countA}</div>
                    <div className="text-red-700 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-800"></span> OD: {countOD}</div>
                    <div className="text-orange-600 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500"></span> L: {countL}</div>
                    <div className="text-purple-600 flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500"></span> PER: {countPER}</div>
                </div>

                {/* Right: Search and Entries */}
                <div className="flex flex-col sm:flex-row items-center gap-4 w-full xl:w-auto">
                    {/* Entries Per Page */}
                    <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100 w-full sm:w-auto justify-center">
                        <div className="relative">
                            <select
                                value={entriesPerPage}
                                onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                                className="text-sm border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500 pl-3 pr-8 py-1.5 outline-none cursor-pointer bg-white font-bold appearance-none shadow-sm"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={999999}>All</option>
                            </select>
                            {/* <FiChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={14} /> */}
                        </div>
                        <label className="text-sm font-medium text-gray-600">Entries</label>
                    </div>

                    {/* Search Bar */}
                    <div className="relative w-full sm:w-72">
                        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name, shift, status..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 text-sm border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 bg-white shadow-sm"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <FiXCircle />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Bulk Actions Banner */}
            {selectedIds.length > 0 && (
                <div className="mb-6 bg-red-50/80 border border-red-100 p-4 sm:p-5 rounded-2xl flex flex-col xl:flex-row items-center justify-between gap-5 animate-fade-in-down backdrop-blur-sm">
                    <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-center sm:text-left">
                        <div className="bg-[#C30010] text-white text-xs font-black px-3 py-1.5 rounded-full shadow-lg shadow-red-200 ring-4 ring-red-50">
                            {selectedIds.length} Employees Selected
                        </div>
                        <span className="text-sm text-red-900 font-bold tracking-tight">Apply status to the selected workforce:</span>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full xl:w-auto">
                        <select
                            value={bulkStatus}
                            onChange={(e) => setBulkStatus(e.target.value)}
                            className="w-full sm:w-36 text-xs font-black border-red-200 rounded-xl focus:ring-4 focus:ring-red-500/10 focus:border-[#C30010] bg-white shadow-sm uppercase px-4 py-2.5"
                        >
                            <option value="PRESENT">Present</option>
                            <option value="ABSENT">Absent</option>
                            <option value="LATE">Late</option>
                            <option value="ON_LEAVE">On Leave</option>
                            <option value="ON_DUTY">On Duty</option>
                            <option value="HOLIDAY">Holiday</option>
                            <option value="LOP">LOP</option>
                        </select>
                        <input
                            type="text"
                            placeholder="Add memo (Optional)"
                            value={bulkReason}
                            onChange={(e) => setBulkReason(e.target.value)}
                            className="w-full sm:w-48 text-xs font-bold border-red-200 rounded-xl focus:ring-4 focus:ring-red-500/10 focus:border-[#C30010] bg-white shadow-sm placeholder-red-300 py-2.5"
                        />
                        <button
                            onClick={executeBulkAction}
                            disabled={bulkLoading}
                            className="w-full sm:w-auto bg-[#C30010] text-white text-[11px] font-black uppercase tracking-widest px-6 py-2.5 rounded-xl hover:bg-red-800 disabled:opacity-50 shadow-lg shadow-red-200 active:scale-95 transition-all"
                        >
                            {bulkLoading ? 'Applying...' : 'Apply Status'}
                        </button>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border-collapse">
                    <thead className="text-xs uppercase bg-gray-50 text-gray-600 font-bold border-b border-gray-200">
                        <tr>
                            <th className="px-4 py-3 w-10 text-center">
                                <input type="checkbox" onChange={handleSelectAll} checked={paginatedData.length > 0 && selectedIds.length === filteredData.length} />
                            </th>
                            <th className="px-4 py-3 w-16 text-center">No.</th>
                            <th className="px-4 py-3">Name</th>
                            <th className="px-4 py-3">Shift</th>
                            <th className="px-4 py-3">Sched</th>
                            <th className="px-4 py-3 text-center">Days</th>
                            <th className="px-4 py-3 text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {rLoading ? (
                            <tr><td colSpan="7" className="text-center py-8">Loading...</td></tr>
                        ) : paginatedData.length === 0 ? (
                            <tr><td colSpan="7" className="text-center py-8">
                                {searchTerm ? `No results found for "${searchTerm}"` : 'No records found.'}
                            </td></tr>
                        ) : paginatedData.map((row, i) => (
                            <tr key={row.user_id} className={`hover:bg-gray-50 transition-colors ${selectedIds.includes(row.user_id) ? 'bg-red-50' : ''}`}>
                                <td className="px-4 py-3 text-center">
                                    <input type="checkbox" checked={selectedIds.includes(row.user_id)} onChange={() => handleSelectRow(row.user_id)} />
                                </td>
                                <td className="px-4 py-3 text-center text-gray-500">{startIndex + i + 1}</td>
                                <td className="px-4 py-3 font-bold text-gray-800">{row.name}</td>
                                <td className="px-4 py-3 text-gray-600">{row.shift || '-'}</td>
                                <td
                                    className={`px-4 py-3 text-xs text-gray-500 max-w-[280px] break-words ${isHROrAdmin ? 'cursor-pointer hover:bg-yellow-50' : ''}`}
                                    onDoubleClick={() => isHROrAdmin && handlePunchEdit(row)}
                                    title={isHROrAdmin ? "Double click to edit punch" : ""}
                                >
                                    {row.sched_reason ? (
                                        <div className="flex flex-col gap-1">
                                            <span className="text-purple-700 font-bold italic">{row.sched_reason}</span>
                                            {(row.checkIn !== '-' || row.checkOut !== '-') && (
                                                <div className="flex items-center gap-3">
                                                    <span className="font-bold text-gray-700 text-[10px] flex items-center gap-1">In: <span className="text-gray-900">{row.checkIn !== '-' ? renderTimeWithIcon(row.checkIn) : '-'}</span></span>
                                                    <span className="font-bold text-gray-700 text-[10px] flex items-center gap-1">Out: <span className="text-gray-900">{row.checkOut !== '-' ? renderTimeWithIcon(row.checkOut) : '-'}</span></span>
                                                </div>
                                            )}
                                        </div>
                                    ) : ((cleanStatus(row.status) === 'HALF_DAY' || cleanStatus(row.status) === 'PERMISSION' || cleanStatus(row.status) === 'AUTO_PERM') && (row.permission_start_time || row.permission_end_time)) ? (
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-1.5 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-100 w-fit">
                                                <span className="text-purple-700 font-bold uppercase text-[9px]">Timing:</span>
                                                <span className="text-gray-700 font-medium text-[10px]">{row.permission_start_time} - {row.permission_end_time}</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="font-medium text-gray-700 flex items-center gap-1">In: {row.checkIn !== '-' ? renderTimeWithIcon(row.checkIn) : '-'}</span>
                                                <span className="font-medium text-gray-700 flex items-center gap-1">Out: {row.checkOut !== '-' ? renderTimeWithIcon(row.checkOut) : '-'}</span>
                                            </div>
                                        </div>
                                    ) : row.is_manual_override == 1 ? (
                                        <div className="flex flex-col gap-0.5">
                                            <div className="flex items-center gap-1">
                                                <span className="text-blue-600 font-bold italic text-[9px] uppercase bg-blue-50 px-1 rounded border border-blue-100">Manual Override</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="font-medium text-gray-700 text-[11px] flex items-center gap-1">In: {row.checkIn !== '-' ? renderTimeWithIcon(row.checkIn) : '-'}</span>
                                                <span className="font-medium text-gray-700 text-[11px] flex items-center gap-1">Out: {row.checkOut !== '-' ? renderTimeWithIcon(row.checkOut) : '-'}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-0.5">
                                            <div className="flex items-center gap-3">
                                                <span className="font-bold text-gray-700 text-[11px] flex items-center gap-1">In: <span className="text-gray-900">{row.checkIn !== '-' ? renderTimeWithIcon(row.checkIn) : '-'}</span></span>
                                                <span className="font-bold text-gray-700 text-[11px] flex items-center gap-1">Out: <span className="text-gray-900">{row.checkOut !== '-' ? renderTimeWithIcon(row.checkOut) : '-'}</span></span>
                                            </div>
                                            {row.site_name && <span className="text-[9px] text-red-600 font-bold uppercase tracking-tighter mt-0.5">Site: {row.site_name}</span>}
                                            {row.override_reason && <span className="text-[10px] text-pink-600 font-bold italic mt-0.5">{row.override_reason}</span>}
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-center font-bold text-gray-700">{row.present_days}</td>
                                <td className="px-4 py-3 text-center">
                                    <div className="flex justify-center gap-1">
                                        <button
                                            onClick={() => handleManualClick({ ...row, date: registerDate }, 'PRESENT')}
                                            className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-all relative ${cleanStatus(row.status) === 'PRESENT' ? 'bg-green-600 text-white shadow-md' : 'bg-gray-100 text-gray-400 hover:bg-green-50 hover:text-green-600'}`}
                                            title={(row.punch_records && row.punch_records !== '-') ? "Punches detected - Mark Present" : "Mark Present"}
                                        >
                                            P
                                            {cleanStatus(row.status) !== 'PRESENT' && cleanStatus(row.status) !== 'LATE' && row.punch_records && row.punch_records !== '-' && (
                                                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 border-2 border-white rounded-full animate-pulse"></span>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => handleManualClick({ ...row, date: registerDate }, 'LATE')}
                                            className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-all ${cleanStatus(row.status) === 'LATE' ? 'bg-amber-600 text-white shadow-md' : 'bg-gray-100 text-gray-400 hover:bg-amber-100 hover:text-amber-600'}`}
                                            title="Mark Late"
                                        >LT</button>
                                        <button
                                            onClick={() => !isBlockedNonWorkingDay && handleManualClick({ ...row, date: registerDate }, 'ABSENT')}
                                            disabled={isBlockedNonWorkingDay}
                                            className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-all ${isBlockedNonWorkingDay ? 'bg-gray-100 text-gray-300 cursor-not-allowed opacity-70' : (cleanStatus(row.status) === 'ABSENT' || (cleanStatus(row.status) === 'LOP' && (!row.checkIn || row.checkIn === '-' || row.checkIn === '--:--'))) ? 'bg-red-600 text-white shadow-md' : 'bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-600'}`}
                                            title={isBlockedNonWorkingDay ? nonWorkingMessage : 'Absent'}
                                        >A</button>
                                        <button
                                            onClick={() => !isBlockedNonWorkingDay && handleManualClick({ ...row, date: registerDate }, 'ON_LEAVE')}
                                            disabled={isBlockedNonWorkingDay}
                                            className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-all ${isBlockedNonWorkingDay ? 'bg-gray-100 text-gray-300 cursor-not-allowed opacity-70' : cleanStatus(row.status) === 'ON_LEAVE' || cleanStatus(row.status) === 'HALF_DAY' ? 'bg-orange-600 text-white shadow-md' : 'bg-gray-200 text-gray-700 hover:bg-orange-100 hover:text-orange-600'}`}
                                            title={isBlockedNonWorkingDay ? nonWorkingMessage : cleanStatus(row.status) === 'HALF_DAY' ? 'Half Day Leave' : (cleanStatus(row.status) === 'ON_LEAVE' ? 'Full Day Leave' : 'Mark Leave')}
                                        >
                                            {cleanStatus(row.status) === 'HALF_DAY' ? 'HL' : 'L'}
                                        </button>
                                        <button
                                            onClick={() => handleManualClick({ ...row, date: registerDate }, 'ON_DUTY')}
                                            className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-all ${cleanStatus(row.status) === 'ON_DUTY' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-400 hover:bg-blue-100 hover:text-blue-600'}`}
                                            title="On Duty"
                                        >OD</button>
                                        <button
                                            onClick={() => handleManualClick({ ...row, date: registerDate }, 'PERMISSION')}
                                            className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-all ${cleanStatus(row.status) === 'PERMISSION' ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-200 text-gray-700 hover:bg-purple-100 hover:text-purple-600'}`}
                                            title="Permission"
                                        >PER</button>
                                        {isHROrAdmin && ['ON_LEAVE', 'HALF_DAY', 'PERMISSION', 'AUTO_PERM', 'ON_DUTY'].includes(cleanStatus(row.status)) && (
                                            <button
                                                onClick={() => handleCancelStatus({ ...row, date: registerDate })}
                                                className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700 transition-all border border-red-200 shadow-sm active:scale-95"
                                                title="Cancel Leave / Restore State"
                                            >
                                                <FiXCircle size={14} />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody >
                </table >
            </div >

            {/* Pagination Controls */}
            {
                !rLoading && filteredData.length > 0 && (
                    <div className="flex flex-col md:flex-row justify-between items-center mt-4 gap-4 pt-4 border-t border-gray-200">
                        <div className="text-sm text-gray-600">
                            Showing {startIndex + 1} to {endIndex} of {totalEntries} entries
                            {searchTerm && <span className="font-medium text-blue-600"> (filtered from {localData.length} total)</span>}
                        </div>

                        <div className="flex items-center gap-2 sm:gap-3">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="w-9 h-9 flex items-center justify-center text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                            >
                                <FiChevronLeft size={16} />
                            </button>

                            {/* Page Numbers */}
                            <div className="flex gap-1.5">
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) {
                                        pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNum = totalPages - 4 + i;
                                    } else {
                                        pageNum = currentPage - 2 + i;
                                    }

                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`w-9 h-9 flex items-center justify-center text-xs font-black rounded-xl transition-all ${currentPage === pageNum
                                                ? 'bg-[#C30010] text-white shadow-lg shadow-red-100 ring-4 ring-red-50'
                                                : 'text-gray-900 font-bold hover:bg-gray-100 border border-transparent'
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className="w-9 h-9 flex items-center justify-center text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                            >
                                <FiChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )
            }

            <AnimatePresence>
                {showBulkConfirm && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 border border-blue-100"
                        >
                            <div className="flex flex-col items-center text-center">
                                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mb-4">
                                    <FiInfo size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Bulk Update Status?</h3>
                                <p className="text-gray-500 mb-6 text-sm">
                                    Are you sure you want to mark <b>{selectedIds.length} users</b> as <b>{bulkStatus}</b>? This will update their attendance records for the selected date.
                                </p>
                                <div className="flex gap-3 w-full">
                                    <button
                                        onClick={() => setShowBulkConfirm(false)}
                                        className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmBulkAction}
                                        className="flex-1 px-4 py-2 bg-[#C30010] text-white font-bold rounded-lg hover:bg-red-800 shadow-md hover:shadow-lg transition"
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div >
    );
};

// --- Utility: Icon Helper ---
const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return '-';
    // Handle "2026-01-20" or full ISO
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const AttendanceDashboard = () => {
    const { user } = useUser();
    const isHROrAdmin = [1, 9, 13].includes(Number(user?.role_id));

    // VIEW STATE: 'dashboard' | 'report' | 'register' | 'shifts'
    const [currentView, setCurrentView] = useState('dashboard');

    // SHIFT MANAGEMENT STATE
    const [shifts, setShifts] = useState([]);
    const [selectedEmployeeForShift, setSelectedEmployeeForShift] = useState(null);
    const [employeeShiftsHistory, setEmployeeShiftsHistory] = useState([]);
    const [allEmployees, setAllEmployees] = useState([]);
    const [showShiftForm, setShowShiftForm] = useState(false);
    const [editingShift, setEditingShift] = useState(null);
    const [assigningShiftToEmployee, setAssigningShiftToEmployee] = useState(false);
    const [shiftFormDate, setShiftFormDate] = useState(new Date().toISOString().split('T')[0]);
    const [shiftFormReason, setShiftFormReason] = useState('New shift assignment');
    const [selectedShiftIdToAssign, setSelectedShiftIdToAssign] = useState('');
    const [shiftsLoading, setShiftsLoading] = useState(false);
    const [editingEmployeeShift, setEditingEmployeeShift] = useState(null);
    const [shiftFormEndDate, setShiftFormEndDate] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [defaultShiftTarget, setDefaultShiftTarget] = useState(null);
    const [defaultShiftEffectiveDate, setDefaultShiftEffectiveDate] = useState(new Date().toISOString().split('T')[0]);

    // BULK SHIFT ASSIGNMENT STATE
    const [isAssigningBulk, setIsAssigningBulk] = useState(false);
    const [selectedEmployeesForBulk, setSelectedEmployeesForBulk] = useState([]);
    const [bulkShiftFormDate, setBulkShiftFormDate] = useState(new Date().toISOString().split('T')[0]);
    const [bulkShiftFormEndDate, setBulkShiftFormEndDate] = useState('');
    const [bulkShiftFormReason, setBulkShiftFormReason] = useState('Bulk shift assignment');
    const [bulkSelectedShiftId, setBulkSelectedShiftId] = useState('');
    const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);

    useEffect(() => {
        if (shifts.length > 0 && !bulkSelectedShiftId) {
            setBulkSelectedShiftId(shifts[0].id);
        }
    }, [shifts, bulkSelectedShiftId]);

    const employeeOptions = React.useMemo(() => {
        return allEmployees
            .filter(emp => Number(emp.account_status) === 1)
            .map(emp => ({
                value: emp.user_id,
                label: `${emp.first_name} ${emp.last_name} (${emp.designation_name || 'No Designation'})`
            }));
    }, [allEmployees]);

    const handleAssignShiftBulkSubmit = async (e) => {
        e.preventDefault();
        if (selectedEmployeesForBulk.length === 0) {
            toast.error('Please select at least one employee');
            return;
        }
        if (!bulkSelectedShiftId) {
            toast.error('Please select a shift');
            return;
        }

        const t = toast.loading('Assigning shifts in bulk...');
        setIsBulkSubmitting(true);

        try {
            const userIds = selectedEmployeesForBulk.map(emp => emp.value);
            const response = await api.post('/attendance/assign-shift-bulk', {
                user_ids: userIds,
                shift_id: Number(bulkSelectedShiftId),
                effective_date: bulkShiftFormDate,
                end_date: bulkShiftFormEndDate || null,
                reason: bulkShiftFormReason
            });

            if (response.data && response.data.status) {
                toast.success('Bulk shift assignment successful', { id: t });
                setIsAssigningBulk(false);
                setSelectedEmployeesForBulk([]);
                setBulkShiftFormEndDate('');
                setBulkShiftFormReason('Bulk shift assignment');

                // Refresh shift timeline views if currently viewing a single user's history
                if (selectedEmployeeForShift) {
                    fetchEmployeeShiftsHistory(selectedEmployeeForShift.user_id);
                }
            } else {
                throw new Error(response.data?.message || 'Bulk assignment failed');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || 'Failed to assign shifts in bulk', { id: t });
        } finally {
            setIsBulkSubmitting(false);
        }
    };



    const [dashboardDate, setDashboardDate] = useState(new Date().toISOString().split('T')[0]);
    const [registerDate, setRegisterDate] = useState(new Date().toISOString().split('T')[0]);
    const punchMonitorRef = React.useRef(null);
    const activeExportTokenRef = React.useRef(null);
    const [reportFilters, setReportFilters] = useState(() => {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const lastDayStr = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate().toString().padStart(2, '0');
        return {
            startDate: `${y}-${m}-01`,
            endDate: `${y}-${m}-${lastDayStr}`
        };
    });

    // Main filters for stats (now used for dashboard overview)
    const [filters, setFilters] = useState({
        status: 'All',
        designation: 'All',
        facultyName: 'All'
    });

    const [loading, setLoading] = useState(false);
    const [attendanceData, setAttendanceData] = useState([]);
    const [stats, setStats] = useState({ present: 0, absent: 0, late: 0, on_leave: 0, half_day: 0, on_duty: 0, permission: 0 });
    const [reportStats, setReportStats] = useState({ working_days: 0, leaves: 0, permissions: 0, holidays: 0, halfdays: 0, lops: 0 });
    const [missingOutPunchData, setMissingOutPunchData] = useState([]);

    // Detailed Report State
    const [reportData, setReportData] = useState([]);
    const [reportTotal, setReportTotal] = useState(0);
    const [rPage, setRPage] = useState(1);
    const [rLimit, setRLimit] = useState(10);
    const [rSearch, setRSearch] = useState('');
    const [rStatus, setRStatus] = useState('All');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [reportLoading, setReportLoading] = useState(false);
    const [consolidatedData, setConsolidatedData] = useState([]);
    const [selectedEmp, setSelectedEmp] = useState(null); // { id, name } for drill down
    const [consolidatedLoading, setConsolidatedLoading] = useState(false);
    const [consolidatedSummary, setConsolidatedSummary] = useState(null);
    const [gradeFilter, setGradeFilter] = useState('All');

    // Consolidated Pagination State
    const [consolidatedPage, setConsolidatedPage] = useState(1);
    const [consolidatedLimit, setConsolidatedLimit] = useState(10);
    const [isRecalculating, setIsRecalculating] = useState(false);

    // Modal State
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('EDIT'); // 'EDIT' | 'VIEW' | 'EDIT_PUNCH'
    const [modalData, setModalData] = useState(null); // { id, user_id, date, status, name, ...details }
    const [manualReason, setManualReason] = useState('');
    const [editCheckIn, setEditCheckIn] = useState('');
    const [editCheckOut, setEditCheckOut] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [cancelModal, setCancelModal] = useState({ open: false, row: null, reason: '' });

    // Accrued Leave Management Modal State
    const [accruedModal, setAccruedModal] = useState({
        open: false,
        user_id: null,
        name: '',
        designation: '',
        img: '',
        value: '',
        remarks: '',
        loading: false,
        error: null
    });

    // Shift for the currently open half-day modal (fetched dynamically per employee+date)
    const [halfDayShift, setHalfDayShift] = useState(null);

    // Auto-fetch employee shift when half-day or permission modal is shown
    React.useEffect(() => {
        const needsShift = modalOpen && modalData?.user_id &&
            (modalData?.newStatus === 'HALF_DAY' || modalData?.newStatus === 'PERMISSION');
        if (needsShift) {
            const date = modalData.attendance_date || modalData.date || new Date().toISOString().split('T')[0];
            api.get('/attendance/shift-for-date', { params: { user_id: modalData.user_id, date } })
                .then(r => {
                    const shift = r.data?.shift || null;
                    setHalfDayShift(shift);
                    if (modalData?.newStatus === 'HALF_DAY') {
                        const shiftStart = shift?.start_time?.slice(0, 5) || DEFAULT_LATE_THRESHOLD_STR;
                        const splitTime = shift?.half_day_split_time?.slice(0, 5) || DEFAULT_HALF_DAY_SPLIT_STR;
                        setModalData(prev => ({ ...prev, half_day_timing: prev.half_day_timing || 'Morning' }));
                        setEditCheckIn(prev => prev || shiftStart);
                        setEditCheckOut(prev => prev || splitTime);
                    }
                })
                .catch(() => setHalfDayShift(null));
        } else if (!modalOpen) {
            setHalfDayShift(null);
        }
    }, [modalOpen, modalData?.newStatus, modalData?.user_id]);

    // Status Dates Popup State
    const [statusDatesPopup, setStatusDatesPopup] = useState(null); // { userId, userName, status, dates }
    const [statusDatesLoading, setStatusDatesLoading] = useState(false);


    // Load Data
    const fetchData = async () => {
        setLoading(true);
        try {
            const params = { ...filters, status: 'All', startDate: dashboardDate, endDate: dashboardDate };
            const [listRes, reportRes] = await Promise.all([
                api.get('/attendance/data', { params }),
                api.get('/attendance/report-stats', { params })
            ]);

            // Calculate stats from the attendance data directly
            if (listRes.data.status) {
                const data = listRes.data.data || [];
                setAttendanceData(data);

                // Count statuses - same logic as register badges
                const counts = {
                    present: 0,
                    absent: 0,
                    late: 0,
                    on_leave: 0,
                    half_day: 0,
                    on_duty: 0,
                    holiday: 0,
                    permission: 0
                };

                data.forEach(record => {
                    const status = cleanStatus(record.status);

                    switch (status) {
                        case 'PRESENT':
                        case 'WORK_FROM_HOME':
                            counts.present++;
                            break;

                        case 'LATE':
                            counts.present++;
                            counts.late++;
                            break;

                        case 'ABSENT':
                        case 'NOT MARKED':
                        case 'LOP':
                            counts.absent++;
                            break;

                        case 'ON_LEAVE':
                            counts.on_leave++;
                            break;

                        case 'HALF_DAY':
                            counts.half_day++;
                            counts.on_leave++; // Count as 1 person out in dashboard card
                            break;

                        case 'ON_DUTY':
                            counts.on_duty++;
                            counts.present++;
                            break;

                        case 'HOLIDAY':
                            counts.holiday++;
                            break;

                        case 'AUTO_PERM':
                        case 'AUTO PERM':
                            counts.permission++;
                            counts.present++;
                            counts.late++;
                            break;

                        case 'PERMISSION':
                            counts.permission++;
                            counts.present++;
                            if (record.lateVal && record.lateVal !== '-') {
                                counts.late++;
                            }
                            break;
                    }
                });

                setStats(counts);
            }

            if (reportRes.data.status) setReportStats(reportRes.data.data);

        } catch (error) { toast.error('Failed to load data'); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchData(); }, [dashboardDate, filters]);

    const handleMonthChange = (val) => {
        if (!val) return;
        const [year, month] = val.split('-');
        const start = `${year}-${month}-01`;
        const end = new Date(year, parseInt(month), 0).toISOString().split('T')[0];
        setFilters(prev => ({ ...prev, startDate: start, endDate: end }));
        if (currentView === 'report') setRPage(1);
    };

    const shiftMonth = (direction) => {
        const [year, month] = filters.startDate.split('-').map(Number);
        const start = new Date(year, month - 1 + (direction === 'prev' ? -1 : 1), 1);
        const y = start.getFullYear();
        const m = String(start.getMonth() + 1).padStart(2, '0');
        const startDate = `${y}-${m}-01`;
        const endDate = new Date(y, start.getMonth() + 1, 0).toISOString().split('T')[0];

        setFilters(prev => ({ ...prev, startDate, endDate }));
        if (currentView === 'report') setRPage(1);
    };

    const shiftDateRange = (direction) => {
        const start = new Date(filters.startDate);
        const end = new Date(filters.endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const shiftDays = (diffDays === 0 ? 1 : diffDays + 1) * (direction === 'prev' ? -1 : 1);
        start.setDate(start.getDate() + shiftDays);
        end.setDate(end.getDate() + shiftDays);
        setFilters(prev => ({
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0]
        }));
        if (currentView === 'report') setRPage(1);
    };

    // Debounce Search
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(rSearch);
            setConsolidatedPage(1); // Reset page only when search actually commits
        }, 800);
        return () => clearTimeout(handler);
    }, [rSearch]);

    // Fetch Consolidated Report (Employee-wise)
    const fetchConsolidatedReport = async () => {
        if (currentView !== 'report' || selectedEmp) return;
        setConsolidatedLoading(true);
        try {
            const res = await api.get('/attendance/consolidated-report', {
                params: {
                    startDate: reportFilters.startDate,
                    endDate: reportFilters.endDate,
                    search: debouncedSearch,
                    grade: gradeFilter
                }
            });
            if (res.data.status) {
                setConsolidatedData(res.data.data);
                setConsolidatedSummary(res.data.summary);
            }
        } catch (e) { toast.error('Failed to load summary report'); }
        finally { setConsolidatedLoading(false); }
    };

    useEffect(() => { fetchConsolidatedReport(); }, [currentView, reportFilters, debouncedSearch, selectedEmp, gradeFilter]);


    // Fetch Detailed Report logic (Now used for drill-down or standard list)
    useEffect(() => {
        const fetchDetailedReport = async () => {
            // Determine active view context
            let start = registerDate;
            let end = registerDate;
            let isReportContext = false;

            if (currentView === 'dashboard') {
                start = dashboardDate;
                end = dashboardDate;
            } else if (currentView === 'report' && selectedEmp) {
                start = reportFilters.startDate;
                end = reportFilters.endDate;
                isReportContext = true;
            } else if (currentView === 'register') {
                start = registerDate;
                end = registerDate;
            } else {
                // Return if not in a valid context for this fetch
                return;
            }

            // In reports, only fetch if emp is selected. In others, always fetch if view is active.
            if (currentView === 'report' && !selectedEmp) return;

            setReportLoading(true);
            try {
                const res = await api.get('/attendance/detailed-report', {
                    params: {
                        startDate: start,
                        endDate: end,
                        page: rPage,
                        limit: rLimit,
                        search: isReportContext ? '' : debouncedSearch,
                        status: isReportContext ? 'All' : rStatus,
                        user_id: selectedEmp ? selectedEmp.id : null
                    }
                });
                if (res.data.status) {
                    setReportData(res.data.data);
                    setReportTotal(res.data.total);
                }
            } catch (e) { toast.error('Failed to load logs'); }
            finally { setReportLoading(false); }
        };
        fetchDetailedReport();
    }, [currentView, reportFilters, registerDate, dashboardDate, rPage, rLimit, debouncedSearch, rStatus, selectedEmp]);

    const fetchShifts = async () => {
        setShiftsLoading(true);
        try {
            const res = await api.get('/attendance/shifts');
            setShifts(res.data || []);
        } catch (e) {
            toast.error('Failed to load shifts');
        } finally {
            setShiftsLoading(false);
        }
    };

    const fetchEmployeesForShifts = async () => {
        const isHROrAdmin = user && (user.role_id === 1 || user.role_id === '1');
        const designationId = Number(user?.designation_id ?? 0);
        const isVerticalHead = [25, 26, 42, 43, 24].includes(designationId) || Number(user?.id ?? user?.user_id ?? 0) === 111;
        if (!isHROrAdmin && !isVerticalHead) return;
        try {
            const res = await api.get('/hrms/employee-list');
            if (res.data.status) {
                setAllEmployees(res.data.data || []);
            }
        } catch (e) {
            toast.error('Failed to load employee list');
        }
    };

    const fetchEmployeeShiftsHistory = async (userId) => {
        try {
            const res = await api.get(`/attendance/employee-shifts`, { params: { user_id: userId } });
            setEmployeeShiftsHistory(res.data || []);
        } catch (e) {
            toast.error('Failed to load employee shift history');
        }
    };

    useEffect(() => {
        if (currentView === 'shifts') {
            fetchShifts();
            fetchEmployeesForShifts();
        }
    }, [currentView]);

    const handleSalaryExport = async () => {
        console.log("SALARY EXCEL: Starting export flow...");
        const loadingToast = toast.loading('Generating Salary Report Excel...');
        try {
            console.log("SALARY EXCEL: Fetching data from API with filters:", {
                startDate: reportFilters.startDate,
                endDate: reportFilters.endDate,
                search: debouncedSearch,
                grade: gradeFilter
            });
            const res = await api.get('/attendance/salary-report', {
                params: {
                    startDate: reportFilters.startDate,
                    endDate: reportFilters.endDate,
                    search: debouncedSearch,
                    grade: gradeFilter
                }
            });

            console.log("SALARY EXCEL: Received response status:", res?.status, "payload status:", res?.data?.status);

            if (res.data.status) {
                console.log("SALARY EXCEL: Generating Excel workbook, data length:", res.data.data?.length);
                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet('Salary Attendance Report');

                let graceStart = '09:06';
                let graceEnd = '09:15';
                let lateAbove = '9:16AM';

                if (res.data.data && res.data.data.length > 0) {
                    const firstRow = res.data.data[0];
                    const lateStr = firstRow.late_threshold || '09:06:00';
                    const permStr = firstRow.permission_threshold || '09:16:00';

                    try {
                        const lateParts = lateStr.split(':');
                        graceStart = `${lateParts[0]}:${lateParts[1]}`;

                        const permParts = permStr.split(':');
                        const permMins = parseInt(permParts[0]) * 60 + parseInt(permParts[1]);
                        const graceEndMins = permMins - 1;
                        const h2 = String(Math.floor(graceEndMins / 60)).padStart(2, '0');
                        const m2 = String(graceEndMins % 60).padStart(2, '0');
                        graceEnd = `${h2}:${m2}`;

                        const hGrace = Math.floor(graceEndMins / 60);
                        const mGrace = String(graceEndMins % 60).padStart(2, '0');
                        const hour12 = (hGrace % 12) || 12;
                        const ampm = hGrace >= 12 ? 'PM' : 'AM';
                        lateAbove = `${hour12}:${mGrace}${ampm}`;
                    } catch (e) {
                        console.error("Failed to parse shift timings dynamically:", e);
                    }
                }

                // Title Headers
                worksheet.mergeCells('A1:V1');
                const titleCell = worksheet.getCell('A1');
                titleCell.value = 'eNova Software and Hardware Solutions P Ltd';
                titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
                titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFF0000' } };

                worksheet.mergeCells('A2:V2');
                const subCell = worksheet.getCell('A2');
                subCell.value = 'Attendance Calculation Format';
                subCell.alignment = { vertical: 'middle', horizontal: 'center' };
                subCell.font = { name: 'Arial', size: 12, bold: true };

                worksheet.mergeCells('A3:V3');
                const dateCell = worksheet.getCell('A3');
                dateCell.value = `Period: ${reportFilters.startDate} to ${reportFilters.endDate}`;
                dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
                dateCell.font = { name: 'Arial', size: 10, italic: true };

                worksheet.addRow([]);

                // Table Headers
                const headerRow = worksheet.addRow([
                    'No.',
                    'Employee Name',
                    'Designation',
                    'Work Days',
                    'Present',
                    'Scheduled Leave',
                    'Unscheduled Leave',
                    'Absent(No notification)',
                    'No of Days Leave',
                    'LOP for Leave',
                    'Permission',
                    'Late Punch',
                    `No of Days Late ${graceStart} to ${graceEnd}`,
                    `No of Days Late above ${lateAbove}`,
                    'LOP for Late Punch',
                    'Dates Missing Status Report',
                    'No of Days of MSR',
                    'LOP for MSR',
                    'Total LOP',
                    'This month CL',
                    'Leave Blce from Last month',
                    'Final Number of Days Present'
                ]);

                headerRow.eachCell(cell => {
                    cell.font = { bold: true, color: { argb: 'FF000000' } }; // Black
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } }; // Light Gray
                    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                    headerRow.height = 40;
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                });

                // Data Rows
                res.data.data.forEach((r, i) => {
                    const row = worksheet.addRow([
                        i + 1,
                        r.name,
                        r.designation,
                        r.working_days,
                        r.present,
                        r.scheduled_leave,
                        r.unscheduled_leave,
                        r.absent,
                        r.no_of_days_leave,
                        r.lop_for_leave,
                        r.permission,
                        r.late_punch,
                        r.late_05_15_count,
                        r.late_above_15_count,
                        r.lop_for_late_punch,
                        r.dates_missing_status_report,
                        r.no_of_days_msr,
                        r.lop_for_msr,
                        r.total_lop,
                        r.this_month_cl,
                        r.leave_balance_last_month,
                        r.final_present_days
                    ]);
                    row.height = 45;
                    row.eachCell((cell, colNumber) => {
                        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                        if ((colNumber === 8 || colNumber === 10 || colNumber === 19) && parseFloat(cell.value) > 0) {
                            cell.font = { bold: true, color: { argb: 'FFFF0000' } };
                        }
                    });
                });

                // Set Columns Width
                worksheet.columns = [
                    { width: 6 },   // No.
                    { width: 25 },  // Employee
                    { width: 25 },  // Designation
                    { width: 12 },  // Work Days
                    { width: 10 },  // Present
                    { width: 16 },  // Scheduled Leave
                    { width: 18 },  // Unscheduled Leave
                    { width: 22 },  // Absent(No notification)
                    { width: 16 },  // No of Days Leave
                    { width: 14 },  // LOP for Leave
                    { width: 14 },  // Permission
                    { width: 30 },  // Late Punch
                    { width: 26 },  // No of Days Late (Grace Window)
                    { width: 26 },  // No of Days Late (> Grace Window)
                    { width: 18 },  // LOP for Late Punch
                    { width: 26 },  // Dates Missing Status Report
                    { width: 16 },  // No of Days MSR
                    { width: 14 },  // LOP for MSR
                    { width: 14 },  // Total LOP
                    { width: 14 },  // This month CL
                    { width: 24 },  // Leave Blce from Last month
                    { width: 26 }   // Final Number of Days Present
                ];

                console.log("SALARY EXCEL: Writing workbook buffer...");
                const buffer = await workbook.xlsx.writeBuffer();
                console.log("SALARY EXCEL: Buffer size:", buffer.length);
                const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

                // Fallback to anchor click if saveAs fails or is blocked
                console.log("SALARY EXCEL: Triggering download...");
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Salary_Attendance_Report_${reportFilters.startDate}.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                console.log("SALARY EXCEL: Download trigger completed.");

                toast.success('Salary Report Excel Exported', { id: loadingToast });
            } else {
                console.warn("SALARY EXCEL: Response status was not true:", res.data);
                toast.error('Failed to generate salary report data', { id: loadingToast });
            }
        } catch (e) {
            console.error("SALARY EXCEL ERROR:", e);
            toast.error('Export Failed', { id: loadingToast });
        }
    };

    const handleExportDetailedExcel = async () => {
        // ── Snapshot state at the exact moment of click ──
        // This prevents race conditions when the user rapidly switches
        // between employees: the export always uses the data that was
        // visible when the button was clicked, not whatever arrives later.
        const snapEmp = selectedEmp;  //updated code
        const snapData = reportData ? [...reportData] : [];
        const snapFilters = { ...reportFilters };

        if (!snapEmp || snapData.length === 0) {
            toast.error('No data to export');
            return;
        }

        // ── Unique token per export click ──
        // If a newer export is triggered before this one finishes,
        // the stale export silently aborts before saving the file.
        const exportToken = `${Date.now()}_${snapEmp.id}`;
        activeExportTokenRef.current = exportToken;

        const loadingToast = toast.loading('Generating Detailed Excel...');
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Detailed Logs');

            // 1. Company Name & Profile
            worksheet.mergeCells('A1:J1');
            const titleCell = worksheet.getCell('A1');
            titleCell.value = 'eNova Software and Hardware Solutions P Ltd';
            titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
            titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFF0000' } };

            // 2. Report Subtitle
            worksheet.mergeCells('A2:J2');
            const subtitleCell = worksheet.getCell('A2');
            subtitleCell.value = `Detailed Period Logs: ${snapEmp.name}`;
            subtitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
            subtitleCell.font = { name: 'Arial', size: 12, bold: true };

            // 3. Date Range
            worksheet.mergeCells('A3:J3');
            const dateCell = worksheet.getCell('A3');
            dateCell.value = `Period: ${formatDateDDMMYYYY(snapFilters.startDate)} — ${formatDateDDMMYYYY(snapFilters.endDate)}`;
            dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
            dateCell.font = { name: 'Arial', size: 10, italic: true };

            worksheet.addRow([]); // Spacer

            // 4. Headers
            const headerRow = worksheet.addRow([
                '#', 'Date', 'In', 'Out', 'Permission', 'Late/Deviation', 'OT', 'Production', 'Punches', 'Status'
            ]);
            headerRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });

            // 5. Data Rows — use snapData (frozen at click time)
            snapData.forEach((d, idx) => {
                const prod = calculateProduction(d.check_in, d.check_out);
                const late = (d.lateVal !== undefined && d.lateVal !== null) ? d.lateVal : calculateLate(d.check_in);
                const ot = (d.otVal !== undefined && d.otVal !== null) ? d.otVal : calculateOvertime(d.check_out);
                const per = (d.permission_start_time || d.permission_end_time) ? `${d.permission_start_time || ''} - ${d.permission_end_time || ''}` : '-';

                const hasPunches = d.check_in && d.check_in !== '--:--' && d.check_in !== '-';
                let expStatus = d.status || 'ABSENT';
                if (d.status === 'LOP') {
                    expStatus = hasPunches ? 'LATE (LOP)' : 'ABSENT';
                } else if (d.status === 'ABSENT' && hasPunches) {
                    expStatus = 'LATE';
                }

                const row = worksheet.addRow([
                    idx + 1,
                    formatDateDDMMYYYY(d.attendance_date),
                    d.check_in || '--:--',
                    d.check_out || '--:--',
                    per,
                    late,
                    ot,
                    prod,
                    d.punch_records || 'No Logs',
                    expStatus
                ]);

                row.eachCell((cell, colNumber) => {
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

                    // Conditional Color for Status
                    if (colNumber === 10) {
                        const status = cell.value;
                        if (status === 'PRESENT' || status === 'WORK_FROM_HOME') {
                            cell.font = { color: { argb: 'FF059669' }, bold: true };
                        } else if (status === 'ABSENT' || status === 'NOT MARKED') {
                            cell.font = { color: { argb: 'FFDC2626' }, bold: true };
                        } else if (status === 'ON_LEAVE' || status === 'LOP' || status === 'LEAVE' || status === 'HALF_DAY_LEAVE') {
                            cell.font = { color: { argb: 'FF7C3AED' }, bold: true };
                        } else if (status === 'HALF_DAY') {
                            cell.font = { color: { argb: 'FFEA580C' }, bold: true };
                        } else if (status === 'LATE') {
                            cell.font = { color: { argb: 'FFB45309' }, bold: true };
                        } else if (status === 'PERMISSION') {
                            cell.font = { color: { argb: 'FFBE185D' }, bold: true };
                        } else if (status === 'AUTO PERM' || status === 'AUTO_PERM' || status === 'AUTO') {
                            cell.font = { color: { argb: 'FF6D28D9' }, bold: true };
                        } else if (status === 'ON_DUTY' || status === 'OD') {
                            cell.font = { color: { argb: 'FF4338CA' }, bold: true };
                        } else if (status === 'WENT_LATE_YESTERDAY') {
                            cell.font = { color: { argb: 'FF0369A1' }, bold: true };
                        } else if (status === 'HOLIDAY') {
                            cell.font = { color: { argb: 'FF1D4ED8' }, bold: true };
                        }
                    }
                });
            });

            // 6. Autofit columns
            worksheet.columns.forEach(column => { column.width = column.header ? column.header.length + 10 : 15; });

            const buffer = await workbook.xlsx.writeBuffer();

            // ── Cancel-token check before saving ──
            // If a newer export was triggered, discard this one silently.
            if (activeExportTokenRef.current !== exportToken) {
                toast.dismiss(loadingToast);
                return;
            }

            saveAs(new Blob([buffer]), `${snapEmp.name}_Detailed_Logs_${formatDateDDMMYYYY(snapFilters.startDate)}.xlsx`);
            toast.success('Excel Generated!');
        } catch (e) {
            console.error(e);
            toast.error('Failed to generate Excel');
        } finally {
            toast.dismiss(loadingToast);
        }
    };


    // Recalculate
    const handleRecalculate = async () => {
        if (isRecalculating) return;
        setIsRecalculating(true);
        const t = toast.loading('Calculating...');

        let start = dashboardDate;
        let end = dashboardDate;

        if (currentView === 'report') {
            start = reportFilters.startDate;
            end = reportFilters.endDate;
        } else if (currentView === 'register') {
            start = registerDate;
            end = registerDate;
        }

        try {
            await api.post('/attendance/recalculate', {
                start_date: start,
                end_date: end
            });
            toast.success('Synced successfully', { id: t });

            // Refresh logic based on view
            setRefreshTrigger(prev => prev + 1); // Triggers RegisterView
            fetchData(); // Triggers Dashboard View
        } catch (e) {
            toast.error('Failed to sync', { id: t });
        } finally {
            setIsRecalculating(false);
        }
    };

    // Manual Update Handler (Popup)
    const handleManualClick = (row, targetStatus) => {
        if (cleanStatus(row.status) === targetStatus) {
            setModalData(row);
            setModalMode('VIEW');
            setModalOpen(true);
        } else {
            setModalData({ ...row, newStatus: targetStatus });
            setManualReason(row.override_reason || '');
            setEditCheckIn('');
            setEditCheckOut('');
            setModalMode('EDIT');
            setModalOpen(true);
        }
    };

    // Submit Manual Update
    const submitManualUpdate = async () => {
        if (!manualReason.trim()) {
            toast.error('Please enter a reason');
            return;
        }
        const t = toast.loading('Updating...');
        try {
            const targetStatus = modalData.newStatus || modalData.status;
            const isPermStatus = ['PERMISSION', 'HALF_DAY', 'AUTO_PERM'].includes(cleanStatus(targetStatus));
            const payload = {
                id: modalData.id,
                user_id: modalData.user_id,
                date: modalData.attendance_date || modalData.date, // handle both keys
                status: targetStatus,
                reason: manualReason,
                permission_start_time: isPermStatus ? modalData.permission_start_time : null,
                permission_end_time: isPermStatus ? modalData.permission_end_time : null
            };

            if (modalMode === 'EDIT_PUNCH') {
                payload.check_in_time = editCheckIn;
                payload.check_out_time = editCheckOut;
            }

            if (modalData.newStatus === 'HALF_DAY') {
                const shiftStart = halfDayShift?.start_time?.slice(0, 5) || DEFAULT_LATE_THRESHOLD_STR;
                const splitTime = halfDayShift?.half_day_split_time?.slice(0, 5) || DEFAULT_HALF_DAY_SPLIT_STR;
                // For Half Day, store the leave window in permission fields to preserve biometric data
                payload.permission_start_time = editCheckIn || shiftStart;
                payload.permission_end_time = editCheckOut || splitTime;
                payload.half_day_timing = modalData.half_day_timing || 'Morning';
            }

            await api.post('/attendance/update-status', payload);
            toast.success('Updated successfully', { id: t });
            setModalOpen(false);
            setRefreshTrigger(prev => prev + 1);
            fetchData();
        } catch (e) {
            toast.error('Update Failed', { id: t });
        }
    };

    const handleCancelStatus = (row) => {
        setCancelModal({
            open: true,
            row: row,
            reason: ''
        });
    };

    const submitCancelStatus = async () => {
        const { row, reason } = cancelModal;
        if (!reason || !reason.trim()) {
            toast.error('Reason is required to cancel leave');
            return;
        }

        const t = toast.loading('Cancelling leave...');
        try {
            const payload = {
                user_id: row.user_id,
                date: row.attendance_date || row.date || registerDate,
                reason: reason.trim()
            };

            const res = await api.post('/attendance/cancel-leave-status', payload);
            if (res.data.status) {
                toast.success(res.data.message || 'Leave cancelled successfully', { id: t });
                setCancelModal({ open: false, row: null, reason: '' });
                setRefreshTrigger(prev => prev + 1);
                fetchData();
                if (typeof fetchRegisterData === 'function') {
                    fetchRegisterData();
                }
                if (typeof fetchConsolidatedReport === 'function') {
                    fetchConsolidatedReport();
                }
                // If statusDatesPopup is open and we just cancelled a date from it, close it
                if (statusDatesPopup && statusDatesPopup.userId === row.user_id) {
                    setStatusDatesPopup(null);
                }
            } else {
                toast.error(res.data.message || 'Failed to cancel leave', { id: t });
            }
        } catch (e) {
            toast.error(e.response?.data?.messages?.error || 'Failed to cancel leave', { id: t });
        }
    };

    // Handle Status Badge Click (Late, LOP, Leave, Absent)
    const handleStatusClick = async (userId, userName, status) => {
        setStatusDatesLoading(true);
        setStatusDatesPopup({ userId, userName, status, dates: [] });

        const sDate = currentView === 'report' ? reportFilters.startDate : dashboardDate;
        const eDate = currentView === 'report' ? reportFilters.endDate : dashboardDate;

        try {
            const res = await api.get('/attendance/status-dates', {
                params: {
                    user_id: userId,
                    status: status,
                    startDate: sDate,
                    endDate: eDate
                }
            });

            if (res.data.status) {
                setStatusDatesPopup({
                    userId,
                    userName,
                    status,
                    dates: res.data.data
                });
            }
        } catch (e) {
            toast.error('Failed to load dates');
        } finally {
            setStatusDatesLoading(false);
        }
    };

    // Handle Accrued Leave Update Submission
    const handleSaveAccruedLeave = async (e) => {
        e.preventDefault();
        if (!accruedModal.user_id) return;

        const val = parseFloat(accruedModal.value);
        if (isNaN(val) || val < 0) {
            setAccruedModal(prev => ({ ...prev, error: 'Please enter a valid non-negative accrued leave value' }));
            return;
        }

        try {
            setAccruedModal(prev => ({ ...prev, loading: true, error: null }));
            const res = await api.post('/hrms/admin/update-accrued-leave', {
                user_id: accruedModal.user_id,
                accrued_leave: val,
                remarks: accruedModal.remarks
            });

            if (res.data?.status) {
                toast.success(res.data.message || 'Accrued leave updated successfully!');
                setAccruedModal({ open: false, user_id: null, name: '', designation: '', img: '', value: '', remarks: '', loading: false, error: null });
                if (typeof fetchConsolidatedReport === 'function') {
                    fetchConsolidatedReport();
                }
            } else {
                setAccruedModal(prev => ({ ...prev, loading: false, error: res.data?.message || 'Failed to update accrued leave' }));
            }
        } catch (err) {
            console.error('Error updating accrued leave:', err);
            setAccruedModal(prev => ({
                ...prev,
                loading: false,
                error: err.response?.data?.message || err.message || 'Error updating accrued leave'
            }));
        }
    };

    const handleExport = async (type) => {
        const generatedDate = new Date().toLocaleString();

        if (!selectedEmp) {
            // --- EXPORT CONSOLIDATED REPORT ---
            if (type === 'EXCEL') {
                const loadingToast = toast.loading('Generating Excel...');
                try {
                    const workbook = new ExcelJS.Workbook();
                    const worksheet = workbook.addWorksheet('Attendance Summary');

                    // 1. Headers
                    worksheet.mergeCells('A1:O1');
                    const titleCell = worksheet.getCell('A1');
                    titleCell.value = 'eNova Software and Hardware Solutions P Ltd';
                    titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
                    titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFF0000' } };

                    worksheet.mergeCells('A2:O2');
                    const subCell = worksheet.getCell('A2');
                    subCell.value = 'Attendance Summary Report';
                    subCell.alignment = { vertical: 'middle', horizontal: 'center' };
                    subCell.font = { name: 'Arial', size: 12, bold: true };

                    worksheet.mergeCells('A3:O3');
                    const dateCell = worksheet.getCell('A3');
                    dateCell.value = `Period: ${reportFilters.startDate} to ${reportFilters.endDate}`;
                    dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
                    dateCell.font = { name: 'Arial', size: 10, italic: true };

                    worksheet.addRow([]);

                    // Table Header
                    const headerRow = worksheet.addRow(['No.', 'Employee', 'Designation', 'Work Days', 'Accrued Leaves', 'Present', 'Late', 'LOP', 'Total Leave', 'Scheduled Leave', 'Unscheduled Leave', 'Perm', 'OD', 'Absent', 'Missing Status', 'Atten %']);
                    headerRow.eachCell(cell => {
                        cell.font = { bold: true, color: { argb: 'FF000000' } }; // Black
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } }; // Light Gray
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                    });

                    // Data
                    consolidatedData.forEach((r, i) => {
                        const row = worksheet.addRow([
                            i + 1, r.name, r.designation, r.working_days, Math.max(0, r.accrued_leave || 0), r.present, r.late, r.lop || 0, r.leave || 0, r.scheduled_leave || 0, r.unscheduled_leave || 0, r.permission || 0, r.od || 0, r.absent, r.missing_status || 0, r.percentage + '%'
                        ]);
                        row.eachCell((cell, colNumber) => {
                            // Left align Name (Col 2) and Designation (Col 3)
                            const isText = colNumber === 2 || colNumber === 3;
                            cell.alignment = { horizontal: isText ? 'left' : 'center', vertical: 'middle' };
                            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                        });
                    });

                    // Column Widths (15 columns)
                    worksheet.columns = [
                        { width: 8 }, { width: 25 }, { width: 25 }, { width: 12 }, { width: 15 }, { width: 10 }, { width: 10 }, { width: 10 }, { width: 17 }, { width: 19 }, { width: 10 }, { width: 10 }, { width: 10 }, { width: 16 }, { width: 12 }
                    ];

                    const buffer = await workbook.xlsx.writeBuffer();
                    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    saveAs(blob, `Attendance_Summary_${reportFilters.startDate}.xlsx`);
                    toast.success('Excel Exported', { id: loadingToast });
                } catch (e) { toast.error('Export Failed', { id: loadingToast }); }

            } else {
                // PDF
                const doc = new jsPDF('landscape');

                autoTable(doc, {
                    startY: 35,
                    margin: { top: 35 },
                    headStyles: {
                        fillColor: [229, 231, 235], // Light Gray
                        textColor: [0, 0, 0],       // Black
                        halign: 'center',
                        fontStyle: 'bold',
                        lineWidth: 0.1,
                        lineColor: [200, 200, 200]
                    },
                    bodyStyles: {
                        halign: 'center',
                        textColor: [0, 0, 0],       // Black
                        lineWidth: 0.1,
                        lineColor: [200, 200, 200]
                    },
                    columnStyles: {
                        1: { halign: 'left' }, // Name
                        2: { halign: 'left' }  // Designation
                    },
                    head: [['No.', 'Employee', 'Designation', 'Work Days', 'Accrued', 'Pres', 'Late', 'LOP', 'Leave', 'Sched Leave', 'Unsched Leave', 'Perm', 'OD', 'Abs', 'Missing', '%']],
                    body: consolidatedData.map((r, i) => [
                        i + 1, r.name, r.designation, r.working_days, Math.max(0, r.accrued_leave || 0), r.present, r.late, r.lop || 0, r.leave || 0, r.scheduled_leave || 0, r.unscheduled_leave || 0, r.permission || 0, r.od || 0, r.absent, r.missing_status || 0, r.percentage + '%'
                    ]),
                    didDrawPage: (data) => {
                        // Header
                        doc.setFontSize(18);
                        doc.setTextColor(216, 0, 0);
                        doc.text("eNova Software and Hardware Solutions P Ltd", doc.internal.pageSize.width / 2, 15, { align: 'center' });

                        doc.setFontSize(12);
                        doc.setTextColor(0, 0, 0);
                        doc.text("Attendance Summary Report", doc.internal.pageSize.width / 2, 22, { align: 'center' });

                        doc.setFontSize(10);
                        doc.text(`Period: ${reportFilters.startDate} to ${reportFilters.endDate}`, doc.internal.pageSize.width / 2, 28, { align: 'center' });

                        // Footer
                        const pageSize = doc.internal.pageSize;
                        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
                        doc.setFontSize(8);
                        doc.setTextColor(0, 0, 0);
                        doc.text(`Generated on: ${generatedDate}`, 14, pageHeight - 10);
                        doc.text(`Page ${data.pageNumber}`, pageSize.width - 20, pageHeight - 10);
                    }
                });
                doc.save(`Attendance_Summary_${reportFilters.startDate}.pdf`);
            }
        } else {
            // --- EXPORT DETAILED LOGS (DRILL-DOWN) ---
            if (type === 'EXCEL') {
                const loadingToast = toast.loading('Generating Excel...');
                try {
                    const workbook = new ExcelJS.Workbook();
                    const worksheet = workbook.addWorksheet('Daily Logs');

                    // Headers
                    worksheet.mergeCells('A1:F1');
                    const titleCell = worksheet.getCell('A1');
                    titleCell.value = 'eNova Software and Hardware Solutions P Ltd';
                    titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
                    titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFF0000' } };

                    worksheet.mergeCells('A2:F2');
                    const subCell = worksheet.getCell('A2');
                    subCell.value = `Daily Logs: ${selectedEmp.name}`;
                    subCell.alignment = { vertical: 'middle', horizontal: 'center' };
                    subCell.font = { name: 'Arial', size: 12, bold: true };

                    worksheet.mergeCells('A3:F3');
                    const dateCell = worksheet.getCell('A3');
                    dateCell.value = `Period: ${reportFilters.startDate} to ${reportFilters.endDate}`;
                    dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
                    dateCell.font = { name: 'Arial', size: 10, italic: true };

                    worksheet.addRow([]);

                    const headerRow = worksheet.addRow(['Date', 'Check In', 'Check Out', 'Late', 'Status', 'Punch Records']);
                    headerRow.eachCell(cell => {
                        cell.font = { bold: true, color: { argb: 'FF000000' } }; // Black
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } }; // Light Gray
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                    });

                    reportData.forEach(r => {
                        const row = worksheet.addRow([
                            r.attendance_date, r.check_in, r.check_out, calculateLate(r.check_in), r.status, r.punch_records
                        ]);
                        row.eachCell((cell, colNumber) => {
                            // Status (Col 5) to Left, others Center
                            // Note: Punch Records (Col 6) usually long text, maybe left too? Sticking to Status left for now as requested.
                            const isStatus = colNumber === 5;
                            cell.alignment = { horizontal: isStatus ? 'left' : 'center', vertical: 'middle' };
                            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

                            // Conditional Color for Status
                            if (colNumber === 5) {
                                const status = cell.value;
                                if (status === 'PRESENT' || status === 'WORK_FROM_HOME') {
                                    cell.font = { color: { argb: 'FF059669' }, bold: true };
                                } else if (status === 'ABSENT' || status === 'NOT MARKED') {
                                    cell.font = { color: { argb: 'FFDC2626' }, bold: true };
                                } else if (status === 'ON_LEAVE' || status === 'LOP' || status === 'LEAVE' || status === 'HALF_DAY_LEAVE') {
                                    cell.font = { color: { argb: 'FF7C3AED' }, bold: true };
                                } else if (status === 'HALF_DAY') {
                                    cell.font = { color: { argb: 'FFEA580C' }, bold: true };
                                } else if (status === 'LATE') {
                                    cell.font = { color: { argb: 'FFB45309' }, bold: true };
                                } else if (status === 'PERMISSION') {
                                    cell.font = { color: { argb: 'FFBE185D' }, bold: true };
                                } else if (status === 'AUTO PERM' || status === 'AUTO_PERM' || status === 'AUTO') {
                                    cell.font = { color: { argb: 'FF6D28D9' }, bold: true };
                                } else if (status === 'ON_DUTY' || status === 'OD') {
                                    cell.font = { color: { argb: 'FF4338CA' }, bold: true };
                                } else if (status === 'WENT_LATE_YESTERDAY') {
                                    cell.font = { color: { argb: 'FF0369A1' }, bold: true };
                                } else if (status === 'HOLIDAY') {
                                    cell.font = { color: { argb: 'FF1D4ED8' }, bold: true };
                                }
                            }
                        });
                    });

                    worksheet.columns = [{ width: 15 }, { width: 15 }, { width: 15 }, { width: 15 }, { width: 15 }, { width: 30 }];

                    const buffer = await workbook.xlsx.writeBuffer();
                    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    saveAs(blob, `Logs_${selectedEmp.name}.xlsx`);
                    toast.success('Excel Exported', { id: loadingToast });
                } catch (e) { toast.error('Export Failed', { id: loadingToast }); }

            } else {
                // PDF
                const doc = new jsPDF();
                autoTable(doc, {
                    startY: 40,
                    margin: { top: 35 },
                    headStyles: {
                        fillColor: [229, 231, 235], // Light Gray
                        textColor: [0, 0, 0],       // Black
                        halign: 'center',
                        fontStyle: 'bold',
                        lineWidth: 0.1,
                        lineColor: [200, 200, 200]
                    },
                    bodyStyles: {
                        halign: 'center',
                        textColor: [0, 0, 0],       // Black
                        lineWidth: 0.1,
                        lineColor: [200, 200, 200]
                    },
                    columnStyles: {
                        4: { halign: 'left' } // Status Left Aligned
                    },
                    head: [['Date', 'In', 'Out', 'Late', 'Status', 'Punches']],
                    body: reportData.map(r => [
                        r.attendance_date, r.check_in, r.check_out, calculateLate(r.check_in), r.status, r.punch_records
                    ]),
                    didDrawPage: (data) => {
                        // Header
                        doc.setFontSize(18);
                        doc.setTextColor(216, 0, 0);
                        doc.text("eNova Software and Hardware Solutions P Ltd", 105, 15, { align: 'center' });

                        doc.setFontSize(12);
                        doc.setTextColor(0, 0, 0);
                        doc.text(`Daily Logs: ${selectedEmp.name}`, 105, 22, { align: 'center' });

                        doc.setFontSize(10);
                        doc.text(`Period: ${reportFilters.startDate} to ${reportFilters.endDate}`, 105, 28, { align: 'center' });

                        // Footer
                        const pageSize = doc.internal.pageSize;
                        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
                        doc.setFontSize(8);
                        doc.setTextColor(0, 0, 0);
                        doc.text(`Generated on: ${generatedDate}`, 14, pageHeight - 10);
                        doc.text(`Page ${data.pageNumber}`, pageSize.width - 20, pageHeight - 10);
                    }
                });
                doc.save(`Logs_${selectedEmp.name}.pdf`);
            }
        }
    };

    const handlePunchEdit = (row) => {
        if (!row) return;

        // Extremely robust time parser for HTML <input type="time">
        const to24h = (timeStr) => {
            if (!timeStr || typeof timeStr !== 'string' || timeStr === '-' || timeStr === '00:00:00') return '';

            try {
                let h, m;
                const clean = timeStr.trim();

                // Case 1: "09:30 AM" or "9:30 AM"
                if (clean.toLowerCase().includes('m')) {
                    const parts = clean.split(/\s+/);
                    if (parts.length < 2) return '';
                    const timePart = parts[0];
                    const period = parts[1].toUpperCase();

                    const [hStr, mStr] = timePart.split(':');
                    h = parseInt(hStr);
                    m = mStr || '00';

                    if (period === 'PM' && h !== 12) h += 12;
                    if (period === 'AM' && h === 12) h = 0;
                } else {
                    // Case 2: "09:30:00" or "09:30"
                    const parts = clean.split(':');
                    h = parseInt(parts[0]);
                    m = parts[1] || '00';
                }

                if (isNaN(h)) return '';
                return `${h.toString().padStart(2, '0')}:${m.toString().substring(0, 2).padStart(2, '0')}`;
            } catch (e) {
                console.error("Time Parse Error:", e);
                return '';
            }
        };

        // 1. Primary Extraction (Try all common keys)
        let cIn = row.checkIn || row.check_in || row.first_punch || '';
        let cOut = row.checkOut || row.check_out || row.last_punch || '';

        // 2. Fallback: Parse from punch_records string ("09:00 AM, 06:00 PM")
        if ((!cIn || cIn === '-') && row.punch_records && row.punch_records !== '-') {
            const records = row.punch_records.split(/[,|]/).map(s => s.trim()).filter(Boolean);
            if (records.length > 0) {
                if (!cIn || cIn === '-') cIn = records[0];
                if ((!cOut || cOut === '-') && records.length > 1) cOut = records[records.length - 1];
            }
        }

        setEditCheckIn(to24h(cIn));
        setEditCheckOut(to24h(cOut));
        setModalData(row);
        setManualReason(row.override_reason || '');
        setModalMode('EDIT_PUNCH');
        setModalOpen(true);
    };

    // --- SHARED HELPERS ---
    const parseTime = (timeStr) => {
        if (!timeStr || timeStr === '-' || !timeStr.trim()) return null;
        // If multiple times, take the relevant one? 
        // Actually, parseTime should handle a single string.
        // We ensure the caller passes a single string by splitting if needed.
        try {
            const cleanStr = timeStr.replace(/\(M\)|\(F\)|\(G\)/g, '').trim();
            const parts = cleanStr.split(' ');
            if (parts.length < 2) return null;
            const [time, modifier] = parts;
            let [hours, minutes] = time.split(':').map(Number);
            if (modifier.includes('PM') && hours < 12) hours += 12;
            if (modifier.includes('AM') && hours === 12) hours = 0;
            return { hours, minutes, totalMinutes: hours * 60 + minutes };
        } catch (e) { return null; }
    };

    const formatDuration = (totalMins) => {
        if (totalMins <= 0) return '-';
        const h = Math.floor(totalMins / 60);
        const m = totalMins % 60;
        if (h === 0) return `${m} Min`;
        if (m === 0) return `${h} ${h === 1 ? 'Hour' : 'Hours'}`;
        return `${h} ${h === 1 ? 'Hr' : 'Hrs'} ${m} Min`;
    };

    const calculateLate = (checkInStr) => {
        if (!checkInStr || checkInStr === '-') return '-';
        // Take the FIRST punch in the list
        const firstPunch = checkInStr.split(',')[0].trim();
        const inTime = parseTime(firstPunch);
        if (!inTime) return '-';
        const startMins = DEFAULT_SHIFT_START_MINS;
        const diff = inTime.totalMinutes - startMins;
        return diff > 0 ? formatDuration(diff) : '-';
    };

    const calculateOvertime = (checkOutStr) => {
        if (!checkOutStr || checkOutStr === '-') return '-';
        // Take the LAST punch in the list
        const punches = checkOutStr.split(',').map(s => s.trim());
        const lastPunch = punches[punches.length - 1];
        const outTime = parseTime(lastPunch);
        if (!outTime) return '-';
        const endMins = DEFAULT_SHIFT_END_MINS;
        const diff = outTime.totalMinutes - endMins;
        return diff > 0 ? formatDuration(diff) : '-';
    };

    const calculateProduction = (checkInStr, checkOutStr) => {
        if (!checkInStr || checkInStr === '-' || !checkOutStr || checkOutStr === '-') return '-';

        const ins = checkInStr.split(',').map(s => s.trim());
        const outs = checkOutStr.split(',').map(s => s.trim());

        let totalMins = 0;
        const pairCount = Math.min(ins.length, outs.length);

        for (let i = 0; i < pairCount; i++) {
            const inTime = parseTime(ins[i]);
            const outTime = parseTime(outs[i]);
            if (inTime && outTime) {
                totalMins += (outTime.totalMinutes - inTime.totalMinutes);
            }
        }

        return totalMins > 0 ? formatDuration(totalMins) : '-';
    };

    // 1. DASHBOARD VIEW
    const DashboardView = () => {
        // State
        const [dPage, setDPage] = useState(1);
        const [dLimit, setDLimit] = useState(10);
        const [dSearch, setDSearch] = useState('');
        const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

        // Filter Handlers
        const handleDashboardDateChange = (val) => {
            setDashboardDate(val);
        };

        const shiftDashboardDate = (direction) => {
            const d = new Date(dashboardDate);
            d.setDate(d.getDate() + (direction === 'prev' ? -1 : 1));
            setDashboardDate(d.toISOString().split('T')[0]);
        };

        // Exports
        const handleExportExcel = async () => {
            const loadingToast = toast.loading('Generating Excel...');
            try {
                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet('Attendance Logs');

                // 1. Company Name
                worksheet.mergeCells('A1:H1');
                const titleCell = worksheet.getCell('A1');
                titleCell.value = 'eNova Software and Hardware Solutions P Ltd';
                titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
                titleCell.font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFFF0000' } }; // Red

                // 2. Report Title
                worksheet.mergeCells('A2:H2');
                const subtitleCell = worksheet.getCell('A2');
                subtitleCell.value = 'Attendance Daily Report';
                subtitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
                subtitleCell.font = { name: 'Arial', size: 12, bold: true };

                // 3. Date
                worksheet.mergeCells('A3:H3');
                const dateCell = worksheet.getCell('A3');
                dateCell.value = `Date: ${formatDateDDMMYYYY(dashboardDate)}`;
                dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
                dateCell.font = { name: 'Arial', size: 10, italic: true };

                // Spacer
                worksheet.addRow([]);

                // 4. Headers
                const headerRow = worksheet.addRow(['Employee', 'Date', 'Check In', 'Status', 'Check Out', 'Late', 'Overtime', 'Production Hours']);
                headerRow.eachCell((cell) => {
                    cell.font = { bold: true, color: { argb: 'FF000000' } }; // Black
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFE5E7EB' } // Light Gray
                    };
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                });
                headerRow.height = 25;

                // 5. Data
                attendanceData.forEach(row => {
                    const r = worksheet.addRow([
                        row.name,
                        row.date,
                        row.checkIn,
                        row.status,
                        row.checkOut,
                        (row.lateVal !== undefined && row.lateVal !== null) ? row.lateVal : calculateLate(row.checkIn),
                        (row.otVal !== undefined && row.otVal !== null) ? row.otVal : calculateOvertime(row.checkOut),
                        calculateProduction(row.checkIn, row.checkOut)
                    ]);

                    r.eachCell((cell, colNumber) => {
                        // Left align name (Col 1) and status (Col 4), others center
                        const isLeft = colNumber === 1 || colNumber === 4;
                        cell.alignment = { horizontal: isLeft ? 'left' : 'center', vertical: 'middle' };
                        cell.border = {
                            top: { style: 'thin' },
                            left: { style: 'thin' },
                            bottom: { style: 'thin' },
                            right: { style: 'thin' }
                        };
                    });
                });

                // 6. Column Widths
                worksheet.columns = [
                    { width: 30 }, // Employee
                    { width: 15 }, // Date
                    { width: 12 }, // Check In
                    { width: 15 }, // Status
                    { width: 12 }, // Check Out
                    { width: 12 }, // Late
                    { width: 12 }, // Overtime
                    { width: 18 }  // Production
                ];

                // Export
                const buffer = await workbook.xlsx.writeBuffer();
                const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                saveAs(blob, `Attendance_Report_${dashboardDate}.xlsx`);
                toast.success('Excel Exported Successfully', { id: loadingToast });
            } catch (err) {
                console.error(err);
                toast.error('Failed to export Excel', { id: loadingToast });
            }
        };

        const handleExportPDF = () => {
            const doc = new jsPDF();
            const dateStr = formatDateDDMMYYYY(dashboardDate);
            const generatedDate = new Date().toLocaleString();

            autoTable(doc, {
                startY: 40,
                margin: { top: 35 },
                headStyles: {
                    fillColor: [229, 231, 235], // Light Gray
                    textColor: [0, 0, 0],       // Black
                    halign: 'center',
                    fontStyle: 'bold',
                    lineWidth: 0.1,
                    lineColor: [200, 200, 200]
                },
                bodyStyles: {
                    halign: 'center',
                    textColor: [0, 0, 0],       // Black
                    lineWidth: 0.1,
                    lineColor: [200, 200, 200]
                },
                columnStyles: {
                    0: { halign: 'left' }, // Employee Name
                    3: { halign: 'left' }  // Status
                },
                alternateRowStyles: { fillColor: [255, 255, 255] },
                head: [['Employee', 'Date', 'In', 'Status', 'Out', 'Late', 'OT', 'Prod']],
                body: attendanceData.map(row => [
                    row.name, row.date, row.checkIn, row.status === 'PERMISSION' && row.lateVal && row.lateVal !== '-' ? 'LATE' : row.status, row.checkOut,
                    (row.lateVal !== undefined && row.lateVal !== null) ? row.lateVal : calculateLate(row.checkIn),
                    (row.otVal !== undefined && row.otVal !== null) ? row.otVal : calculateOvertime(row.checkOut),
                    calculateProduction(row.checkIn, row.checkOut)
                ]),
                didDrawPage: (data) => {
                    // Header
                    // Logo Placeholder (If you have a logo URL, load it here)
                    // if (user?.company_logo) { doc.addImage(...) }

                    // Company Name
                    doc.setFontSize(18);
                    doc.setTextColor(216, 0, 0); // Red
                    doc.text("eNova Software and Hardware Solutions P Ltd", 105, 15, { align: 'center' });

                    // Report Name
                    doc.setFontSize(12);
                    doc.setTextColor(0, 0, 0);
                    doc.text("Attendance Daily Report", 105, 22, { align: 'center' });

                    // Date Checks
                    doc.setFontSize(10);
                    doc.text(`Date: ${dateStr}`, 105, 28, { align: 'center' });

                    // Footer
                    const pageCount = doc.internal.getNumberOfPages();
                    doc.setFontSize(8);
                    doc.setTextColor(0, 0, 0); // Black
                    const pageSize = doc.internal.pageSize;
                    const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();

                    // Generation Date (Left)
                    doc.text(`Generated on: ${generatedDate}`, 14, pageHeight - 10);

                    // Page Number (Right)
                    doc.text(`Page ${data.pageNumber}`, pageSize.width - 20, pageHeight - 10);
                }
            });
            doc.save(`Attendance_Logs_${dashboardDate}.pdf`);
        };

        const handleExportMissingOutPunch = async () => {
            const t = toast.loading('Generating Missing Out Punch Report...');
            try {
                // Fetch missing out punch data from API for the selected dashboard date
                const res = await api.get('/hrms/missing-out-punch', {
                    params: { from: dashboardDate, to: dashboardDate }
                });
                const data = res.data.data || [];
                setMissingOutPunchData(data);

                // Wait for state update and DOM render
                await new Promise(resolve => setTimeout(resolve, 150));

                const element = document.getElementById('missing-outpunch-export-container');
                if (!element) throw new Error('Export container not found');

                element.style.display = 'block';
                const dataUrl = await toJpeg(element, {
                    quality: 1,
                    backgroundColor: '#ffffff',
                    fontEmbedCSS: '',
                    cacheBust: true
                });
                element.style.display = 'none';

                saveAs(dataUrl, `Missing_Out_Punch_Report_${dashboardDate}.jpg`);
                toast.success('Missing Out Punch Report exported successfully', { id: t });
            } catch (e) {
                console.error(e);
                toast.error('Failed to export Missing Out Punch Report', { id: t });
            }
        };

        const handleExportImage = async () => {
            const t = toast.loading('Generating image...');
            try {
                // We create a temporary hidden div with the full table to capture it
                const element = document.getElementById('punch-monitor-export-container');
                if (!element) throw new Error('Export container not found');

                element.style.display = 'block';
                const dataUrl = await toJpeg(element, {
                    quality: 1,
                    backgroundColor: '#ffffff',
                    fontEmbedCSS: '', // Fix SecurityError: Failed to read 'cssRules'
                    cacheBust: true
                });
                element.style.display = 'none';

                saveAs(dataUrl, `Punch_Monitor_${dashboardDate}.jpg`);
                toast.success('Image exported successfully', { id: t });
            } catch (e) {
                console.error(e);
                toast.error('Failed to export image', { id: t });
            }
        };

        const requestSort = (key) => {
            let direction = 'asc';
            if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
            setSortConfig({ key, direction });
        };

        // Stat Cards Calculations (Excludes LATE/AUTO PERM from PRESENT so counts don't overlap)
        const stats = React.useMemo(() => {
            let present = 0, absent = 0, late = 0, on_leave = 0;

            attendanceData.forEach(row => {
                const s = cleanStatus(row.status);
                const isLate = s === 'LATE' || s === 'AUTO_PERM' || s === 'AUTO PERM' || (s === 'PERMISSION' && row.lateVal && row.lateVal !== '-');
                const isAbsent = s === 'ABSENT' || s === 'NOT MARKED' || s === 'NOT_MARKED' || s === 'LOP';
                const isOnLeave = s === 'ON_LEAVE' || s === 'HALF_DAY' || s.includes('LEAVE');
                const isPresent = (s === 'PRESENT' || s === 'WORK_FROM_HOME' || s === 'ON_DUTY' || s === 'OD' || s.includes('DUTY') || s === 'PERMISSION') && !isLate;

                if (isLate) late++;
                else if (isAbsent) absent++;
                else if (isOnLeave) on_leave++;
                else if (isPresent) present++;
            });

            return { present, absent, late, on_leave };
        }, [attendanceData]);

        // Data Processing
        const processedData = React.useMemo(() => {
            let filteredRows = attendanceData.filter(row => {
                const matchesSearch = row.name.toLowerCase().includes(dSearch.toLowerCase()) ||
                    (row.employeeId && row.employeeId.toLowerCase().includes(dSearch.toLowerCase()));

                if (!matchesSearch) return false;

                // Handle Status Filter Locally
                const statusFilter = filters.status;
                if (!statusFilter || statusFilter === 'All') return true;

                const s = cleanStatus(row.status);
                const isLate = s === 'LATE' || s === 'AUTO_PERM' || s === 'AUTO PERM' || (s === 'PERMISSION' && row.lateVal && row.lateVal !== '-');

                if (statusFilter === 'PRESENT') return (s === 'PRESENT' || s === 'WORK_FROM_HOME' || s === 'PERMISSION' || s === 'ON_DUTY' || s === 'OD' || s.includes('DUTY')) && !isLate;
                if (statusFilter === 'ABSENT') return s === 'ABSENT' || s === 'NOT MARKED' || s === 'NOT_MARKED' || s === 'LOP';
                if (statusFilter === 'LATE') return isLate;
                if (statusFilter === 'ON_LEAVE') return s === 'ON_LEAVE' || s === 'HALF_DAY';
                if (statusFilter === 'ON_DUTY') return s === 'ON_DUTY' || s === 'OD' || s.includes('DUTY');
                if (statusFilter === 'PERMISSION') return s === 'PERMISSION';

                return s === statusFilter;
            });

            // Enhance data with calculated fields for sorting
            let result = filteredRows.map(row => ({
                ...row,
                lateVal: (row.lateVal !== undefined && row.lateVal !== null) ? row.lateVal : calculateLate(row.checkIn),
                otVal: (row.otVal !== undefined && row.otVal !== null) ? row.otVal : calculateOvertime(row.checkOut),
                prodVal: row.prodVal && row.prodVal !== '-' ? row.prodVal : calculateProduction(row.checkIn, row.checkOut)
            }));

            const getStatusPriority = (status) => {
                const s = (status || '').toUpperCase();
                if (s.includes('PRESENT')) return 1;
                if (s.includes('HALF')) return 1.5;
                if (s.includes('LATE') && !s.includes('YESTERDAY')) return 2;
                if (s.includes('WENT_LATE_YESTERDAY') || s.includes('WENT LATE')) return 3;
                if (s.includes('DUTY')) return 4;
                if (s.includes('LEAVE')) return 5;
                if (s.includes('PERMISSION')) return 6;
                if (s.includes('HOLIDAY')) return 7;
                if (s.includes('ABSENT')) return 8;
                return 9;
            };

            const parseCheckIn = (timeStr) => {
                if (!timeStr || timeStr === '-') return '99:99';
                // Convert "09:30 AM" to "09:30" or "21:30" for sorting
                const parsed = parseTime(timeStr);
                if (!parsed) return '99:99';
                return `${parsed.hours.toString().padStart(2, '0')}:${parsed.minutes.toString().padStart(2, '0')}`;
            };

            result.sort((a, b) => {
                // If user requested specific sort
                if (sortConfig.key) {
                    let aVal = a[sortConfig.key];
                    let bVal = b[sortConfig.key];
                    if (sortConfig.key === 'checkIn' || sortConfig.key === 'checkOut') {
                        aVal = aVal !== '-' ? aVal : '';
                        bVal = bVal !== '-' ? bVal : '';
                    }
                    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                }

                // Default Sort: Status Priority -> Entry Time
                const orderA = getStatusPriority(a.status);
                const orderB = getStatusPriority(b.status);

                if (orderA !== orderB) return orderA - orderB;

                // Within same status, sort by check-in time
                const timeA = parseCheckIn(a.checkIn);
                const timeB = parseCheckIn(b.checkIn);
                return timeA.localeCompare(timeB);
            });

            return result;
        }, [attendanceData, dSearch, sortConfig]);

        const totalPages = Math.ceil(processedData.length / dLimit) || 1;

        useEffect(() => {
            if (dPage > totalPages && totalPages > 0) {
                setDPage(totalPages);
            }
        }, [dPage, totalPages]);

        const paginatedData = processedData.slice((dPage - 1) * dLimit, dPage * dLimit);

        const getPageNumbers = () => {
            const pages = [];
            let start = Math.max(1, dPage - 2);
            let end = Math.min(totalPages, start + 4);
            if (end - start < 4) start = Math.max(1, end - 4);
            for (let i = start; i <= end; i++) pages.push(i);
            return pages;
        };

        const SortIcon = ({ column }) => {
            if (sortConfig.key !== column) return <span className="text-gray-300 ml-1"><FiChevronUp className="inline w-3 h-3 opacity-0 group-hover:opacity-50" /></span>;
            return sortConfig.direction === 'asc' ? <FiChevronUp className="inline ml-1 text-blue-600" /> : <FiChevronDown className="inline ml-1 text-blue-600" />;
        };

        return (
            <div className="space-y-6 animate-fade-in-up">
                {/* Stat Cards - Keep as is */}
                {/* Stat Cards - Exact UI from Image */}
                {/* Stat Cards - Exact UI from Image */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 mb-8">
                    <SummaryCard
                        title="All"
                        value={attendanceData.length}
                        label="Total Employees"
                        icon={FiUsers}
                        colorClass="text-blue-600"
                        bgColor="bg-blue-50"
                        onClick={() => setFilters(prev => ({ ...prev, status: 'All' }))}
                        highlight={filters.status === 'All'}
                    />
                    <SummaryCard
                        title="Present"
                        value={stats.present}
                        label="Employees on shift"
                        icon={FiCheckCircle}
                        colorClass="text-emerald-600"
                        bgColor="bg-emerald-50"
                        onClick={() => setFilters(prev => ({ ...prev, status: 'PRESENT' }))}
                        highlight={filters.status === 'PRESENT'}
                    />
                    <SummaryCard
                        title="Absent"
                        value={stats.absent}
                        label="Workforce missing"
                        icon={FiXCircle}
                        colorClass="text-rose-600"
                        bgColor="bg-rose-50"
                        onClick={() => setFilters(prev => ({ ...prev, status: 'ABSENT' }))}
                        highlight={filters.status === 'ABSENT'}
                    />
                    <SummaryCard
                        title="Late"
                        value={stats.late}
                        label={`Logged after ${(shifts.find(s => s.is_default) || {}).late_threshold?.slice(0, 5) || DEFAULT_LATE_THRESHOLD_STR}`}
                        icon={FiClock}
                        colorClass="text-amber-600"
                        bgColor="bg-amber-50"
                        onClick={() => setFilters(prev => ({ ...prev, status: 'LATE' }))}
                        highlight={filters.status === 'LATE'}
                    />
                    <SummaryCard
                        title="On Leave"
                        value={stats.on_leave}
                        label="Approved absences"
                        icon={FiCalendar}
                        colorClass="text-purple-600"
                        bgColor="bg-purple-50"
                        onClick={() => setFilters(prev => ({ ...prev, status: 'ON_LEAVE' }))}
                        highlight={filters.status === 'ON_LEAVE'}
                    />
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
                    {/* Toolbar */}
                    <div className="p-6 border-b border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4 bg-white">
                        <div className="flex items-center gap-2">
                            <div className="relative">
                                <select
                                    value={dLimit}
                                    onChange={(e) => { setDLimit(Number(e.target.value)); setDPage(1); }}
                                    className="bg-gray-50 border border-gray-300 text-gray-600 text-sm font-bold rounded-lg focus:ring-blue-500 focus:border-blue-500 py-2 pl-3 pr-8 outline-none cursor-pointer hover:bg-white transition-colors appearance-none"
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={999999}>All</option>
                                </select>
                                {/* <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={16} /> */}
                            </div>
                            <span className="text-sm font-medium text-gray-500">Entries</span>
                        </div>

                        <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-auto">
                            {/* Single Date Navigation */}
                            <div className="flex items-center gap-1 bg-gray-50 p-1 rounded-lg border border-gray-200">
                                <button onClick={() => shiftDashboardDate('prev')} className="p-1.5 hover:bg-white hover:shadow-sm rounded-md transition-all text-gray-600" title="Previous Day">
                                    <FiChevronLeft />
                                </button>
                                <input
                                    type="date"
                                    value={dashboardDate}
                                    onChange={e => handleDashboardDateChange(e.target.value)}
                                    className="border-none bg-transparent text-sm font-black w-32 focus:ring-0 p-1 text-center"
                                />
                                <button onClick={() => shiftDashboardDate('next')} className="p-1.5 hover:bg-white hover:shadow-sm rounded-md transition-all text-gray-600" title="Next Day">
                                    <FiChevronRight />
                                </button>
                            </div>

                            {/* Search */}
                            <div className="relative">
                                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={dSearch}
                                    onChange={e => { setDSearch(e.target.value); setDPage(1); }}
                                    className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 focus:bg-white transition-colors text-sm w-40"
                                />
                            </div>

                            <div className="flex gap-2">
                                <button onClick={handleExportExcel} className="p-2 text-green-600 rounded hover:bg-green-50 transition-colors shadow-sm border border-gray-200" title="Export Excel"><RiFileExcelFill size={20} /></button>
                                <button onClick={handleExportPDF} className="p-2 text-red-600 rounded hover:bg-red-50 transition-colors shadow-sm border border-gray-200" title="Export PDF"><RiFilePdf2Fill size={20} /></button>
                                <button onClick={handleExportMissingOutPunch} className="flex items-center gap-1 p-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition-colors shadow-sm text-xs font-bold" title="Export Missing Out Punch Report as Image">
                                    <FiXCircle size={14} /> Missing Out
                                </button>
                                <button onClick={handleExportImage} className="flex items-center gap-1 p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors shadow-sm text-xs font-bold" title="Daily Punch Report Image">
                                    <FiDownload size={14} /> Image
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-100/50 sticky top-0 z-10 text-gray-600 text-xs font-bold uppercase tracking-wider">
                                <tr>
                                    <th className="px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('name')}>Name <SortIcon column="name" /></th>
                                    <th className="px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('date')}>Date <SortIcon column="date" /></th>
                                    <th className="px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('checkIn')}>Check In <SortIcon column="checkIn" /></th>
                                    <th className="px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('status')}>Status <SortIcon column="status" /></th>
                                    <th className="px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('checkOut')}>Check Out <SortIcon column="checkOut" /></th>
                                    <th className="px-6 py-4">Late</th>
                                    <th className="px-6 py-4">Overtime</th>
                                    <th className="px-6 py-4">Production Hours</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                                {paginatedData.length > 0 ? paginatedData.map((row, i) => (
                                    <tr key={i} className="bg-white hover:bg-blue-50/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                {row.img ? <img
                                                    src={row.img}
                                                    alt=""
                                                    className="w-9 h-9 rounded-full object-cover"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                /> : null}
                                                <div
                                                    className="w-9 h-9 rounded-full bg-gray-800 text-white flex items-center justify-center font-bold text-xs"
                                                    style={{ display: row.img ? 'none' : 'flex' }}
                                                >
                                                    {row.name.charAt(0)}
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-gray-800">{row.name}</span>
                                                    <span className="text-xs text-gray-500">{row.designation || 'Employee'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 font-medium text-gray-600">{formatDateDDMMYYYY(row.date)}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-1 max-w-[150px]">
                                                {row.checkIn && row.checkIn !== '-' ? renderTimeWithIcon(row.checkIn) : <span className="text-gray-400">-</span>}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-1">
                                                    <span className={`px-2.5 py-1 rounded text-xs font-bold w-fit ${row.color}`}>
                                                        {(() => { const hasPunches = row.checkIn && row.checkIn !== '-' && row.checkIn !== '--:--'; const s = cleanStatus(row.status); if (s === 'LOP') return hasPunches ? 'LATE (LOP)' : 'ABSENT'; if (s === 'ABSENT' && hasPunches) return 'LATE'; return row.status || 'ABSENT'; })()}
                                                    </span>
                                                    {row.is_manual_override == 1 && (
                                                        <span className="text-[8px] bg-blue-50 text-blue-600 border border-blue-200 px-1 rounded font-bold italic uppercase">Manual</span>
                                                    )}
                                                </div>
                                                {row.sched_reason && (
                                                    <span className="text-[9px] text-purple-700 font-bold italic leading-none">{row.sched_reason}</span>
                                                )}
                                                {row.site_name && (
                                                    <span className="text-[10px] text-gray-500 font-bold flex items-center gap-1">
                                                        <FiMapPin size={10} className="text-red-500" /> {row.site_name}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-1 max-w-[150px]">
                                                {row.checkOut && row.checkOut !== '-' ? renderTimeWithIcon(row.checkOut) : <span className="text-gray-400">-</span>}
                                            </div>
                                        </td>

                                        {/* Calculated Columns */}
                                        <td className="px-6 py-4 text-gray-500">{row.lateVal}</td>
                                        <td className="px-6 py-4 text-gray-500">{row.otVal}</td>

                                        <td className="px-6 py-4">
                                            {/* Production Badge Style */}
                                            {row.prodVal !== '-' ? (
                                                <span className={`px-2.5 py-1 rounded-md text-xs font-bold text-white shadow-sm flex items-center gap-1 w-fit
                                                    ${parseFloat(row.prodVal) >= 8 ? 'bg-green-500' : 'bg-red-500'}`}>
                                                    <FiClock size={12} /> {row.prodVal}
                                                </span>
                                            ) : <span className="text-gray-400">-</span>}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="8" className="px-6 py-12 text-center text-gray-500">No records found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer */}
                    {
                        totalPages > 1 && (
                            <div className="flex flex-col md:flex-row justify-between items-center p-4 border-t border-gray-200 bg-gray-50 gap-4">
                                <div className="text-xs text-gray-500 font-medium">
                                    Showing {Math.min((dPage - 1) * dLimit + 1, processedData.length)} to {Math.min(dPage * dLimit, processedData.length)} of {processedData.length} entries
                                </div>
                                <div className="flex items-center gap-2 sm:gap-3">
                                    <button
                                        disabled={dPage === 1}
                                        onClick={() => setDPage(p => p - 1)}
                                        className="w-9 h-9 flex items-center justify-center text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                                    >
                                        <FiChevronLeft size={16} />
                                    </button>
                                    <div className="flex gap-1.5 ">
                                        {getPageNumbers().map(p => (
                                            <button
                                                key={p}
                                                onClick={() => setDPage(p)}
                                                className={`w-9 h-9 flex items-center justify-center text-xs font-black rounded-xl transition-all ${dPage === p
                                                    ? 'bg-[#C30010] text-white shadow-lg shadow-red-100 ring-4 ring-red-50'
                                                    : 'text-gray-900 font-bold hover:bg-gray-100 border border-transparent'
                                                    }`}
                                            >
                                                {p}
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        disabled={dPage === totalPages}
                                        onClick={() => setDPage(p => p + 1)}
                                        className="w-9 h-9 flex items-center justify-center text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                                    >
                                        <FiChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )
                    }
                    {/* Hidden Export Container for Image Capture */}
                    <div id="punch-monitor-export-container" className="fixed top-0 left-0 bg-white" style={{ display: 'none', width: '1200px', zIndex: -100 }}>
                        <div className="p-8">
                            <h2 className="text-center text-2xl font-black text-gray-800 mb-1">Employee Punch Monitor</h2>
                            <div className="flex justify-between items-center mb-4 px-2">
                                <span className="text-sm font-bold border-b-2 border-gray-800 pb-1">Date: {formatDateDDMMYYYY(dashboardDate)}</span>
                            </div>

                            <div className="flex gap-4 items-start">
                                {[0, 1].map(halfIndex => {
                                    const midPoint = Math.ceil(processedData.length / 2);
                                    const dataToShow = halfIndex === 0
                                        ? processedData.slice(0, midPoint)
                                        : processedData.slice(midPoint);
                                    const startSNo = halfIndex === 0 ? 0 : midPoint;

                                    if (dataToShow.length === 0) return null;

                                    return (
                                        <div key={halfIndex} className="flex-1">
                                            <table className="w-full border-collapse border border-gray-800 text-[10px]">
                                                <thead>
                                                    <tr className="bg-gray-200">
                                                        <th className="border border-gray-800 px-2 py-1 text-left w-10">SNo.</th>
                                                        <th className="border border-gray-800 px-2 py-1 text-left">Name</th>
                                                        <th className="border border-gray-800 px-2 py-1 text-left w-16">In</th>
                                                        <th className="border border-gray-800 px-2 py-1 text-left">Punch Records</th>
                                                        <th className="border border-gray-800 px-2 py-1 text-left w-20">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {dataToShow.map((row, i) => {
                                                        const s = cleanStatus(row.status);
                                                        let displayStatus = cleanStatus(row.status) === 'LOP' ? 'ABSENT' : row.status?.replace(/_/g, ' ');
                                                        let statusBg = 'bg-white';

                                                        if (s.includes('PRESENT') || s.includes('HALF_DAY') || s.includes('PERMISSION') || s.includes('AUTO')) statusBg = 'bg-gray-300';
                                                        if (s.includes('LATE') || s.includes('WENT_LATE_YESTERDAY') || s.includes('YESTERDAY')) statusBg = 'bg-blue-200';
                                                        if ((s.includes('ABSENT') || s.includes('NOT PRESENT') || s === 'NOT MARKED' || s === 'LOP') && !s.includes('WENT_LATE_YESTERDAY')) {
                                                            const hasPunches = row.checkIn && row.checkIn !== '-' && row.checkIn !== '--:--';
                                                            if (hasPunches) {
                                                                statusBg = 'bg-blue-200';
                                                                displayStatus = (s === 'LOP') ? 'LATE (LOP)' : 'LATE';
                                                            } else {
                                                                statusBg = 'bg-red-600 text-white';
                                                                displayStatus = 'Not Present';
                                                            }
                                                        }
                                                        if (s === 'WENT_LATE_YESTERDAY') {
                                                            statusBg = 'bg-blue-200';
                                                            displayStatus = 'WENT LATE YEST';
                                                        }

                                                        return (
                                                            <tr key={i} className="border border-gray-800">
                                                                <td className="border border-gray-800 px-2 py-1 text-center font-bold bg-gray-50">{startSNo + i + 1}</td>
                                                                <td className="border border-gray-800 px-2 py-1 font-medium">{row.name}</td>
                                                                <td className="border border-gray-800 px-2 py-1 whitespace-nowrap">{row.checkIn === '-' ? '' : row.checkIn?.replace(' AM', '').replace(' PM', '')}</td>
                                                                <td className="border border-gray-800 px-2 py-1">
                                                                    {/* Use punch_records if real data exists, otherwise fall back to checkIn (aggregate stored value) */}
                                                                    {(row.punch_records && row.punch_records !== '-')
                                                                        ? row.punch_records.replace(/ AM/g, '').replace(/ PM/g, '')
                                                                        : (row.checkIn && row.checkIn !== '-'
                                                                            ? row.checkIn.replace(/ AM/g, '').replace(/ PM/g, '')
                                                                            : '-')}
                                                                </td>
                                                                <td className={`border border-gray-800 px-1 py-1 text-center font-bold text-[9px] ${statusBg}`}>
                                                                    {displayStatus}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    {/* Hidden Export Container for Missing Out Punch Image Capture */}
                    <div id="missing-outpunch-export-container" className="fixed top-0 left-0 bg-white" style={{ display: 'none', width: '800px', zIndex: -100 }}>
                        <div className="p-6">
                            <h2 className="text-center text-xl font-bold text-gray-900 mb-2">Enova Software and Hardware Solutions P Ltd.</h2>
                            <div className="bg-[#c30010] text-white text-center py-2 font-black text-sm uppercase tracking-wider rounded-t-lg">
                                Missing Out Punch Report — {formatDateDDMMYYYY(dashboardDate)}
                            </div>

                            <table className="w-full border-collapse border border-gray-300 text-xs">
                                <thead>
                                    <tr className="bg-slate-800 text-white">
                                        <th className="border border-gray-300 px-3 py-2 text-center w-12 font-black">#</th>
                                        <th className="border border-gray-300 px-4 py-2 text-left font-black">Employee Name</th>
                                        <th className="border border-gray-300 px-3 py-2 text-center w-28 font-black">Missing Days</th>
                                        <th className="border border-gray-300 px-3 py-2 text-center w-40 font-black">Dates</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {missingOutPunchData.map((row, idx) => (
                                        <tr key={idx} className={idx % 2 === 1 ? 'bg-red-50/30' : 'bg-white'}>
                                            <td className="border border-gray-300 px-3 py-2 text-center font-bold text-gray-600">{idx + 1}</td>
                                            <td className="border border-gray-300 px-4 py-2 font-semibold text-gray-800">{row.name}</td>
                                            <td className="border border-gray-300 px-3 py-2 text-center font-black text-red-600">{row.count}</td>
                                            <td className="border border-gray-300 px-3 py-2 text-center text-gray-600 font-medium">{row.dates.join(', ')}</td>
                                        </tr>
                                    ))}
                                    {missingOutPunchData.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="border border-gray-300 px-4 py-8 text-center text-gray-500 font-bold text-sm">
                                                No Missing Out Punches for today!
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                {missingOutPunchData.length > 0 && (
                                    <tfoot>
                                        <tr className="bg-slate-800 text-white font-black text-xs">
                                            <td colSpan={2} className="border border-gray-300 px-4 py-2 text-right uppercase tracking-wider">Total</td>
                                            <td className="border border-gray-300 px-3 py-2 text-center">{missingOutPunchData.reduce((acc, row) => acc + row.count, 0)}</td>
                                            <td className="border border-gray-300 px-3 py-2"></td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                </div >
            </div >
        );
    };

    // --- SORTING & PAGINATION LOGIC (Promoted from ReportView) ---
    const [consolidatedSort, setConsolidatedSort] = React.useState({ key: null, direction: 'asc' });

    // Summary Card Component (Inlined) - Updated to match image exactly
    const SummaryCard = ({ title, value, label, icon: Icon, colorClass, bgColor, onClick, highlight }) => {
        return (
            <div
                onClick={onClick}
                className={`group bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between hover:shadow-xl hover:-translate-y-1 transition-all duration-500 relative overflow-hidden ${onClick ? 'cursor-pointer' : ''} ${highlight ? 'ring-2 ring-red-500/20 bg-red-50/10' : ''}`}
            >
                <div className="space-y-2 relative z-10 w-full">
                    <p className="text-[11px] font-bold text-gray-400 capitalize tracking-tight flex items-center gap-1.5">
                        {title}
                    </p>
                    <div className="flex items-end justify-between">
                        <h3 className="text-3xl font-black text-gray-800 tracking-tight">{value}</h3>
                        <div className={`p-3 rounded-2xl ${bgColor} ${colorClass} transition-all duration-500 group-hover:scale-110 group-hover:rotate-6`}>
                            <Icon size={24} />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const requestSort = (key) => {
        let direction = 'asc';
        if (consolidatedSort.key === key && consolidatedSort.direction === 'asc') {
            direction = 'desc';
        }
        setConsolidatedSort({ key, direction });
        setConsolidatedPage(1);
    };

    const SortIcon = ({ column }) => {
        if (consolidatedSort.key !== column) {
            return <span className="text-gray-300 ml-1"><FiChevronUp className="inline w-3 h-3 opacity-0 group-hover:opacity-50" /></span>;
        }
        return consolidatedSort.direction === 'asc' ?
            <FiChevronUp className="inline ml-1 text-[#C30010]" /> :
            <FiChevronDown className="inline ml-1 text-[#C30010]" />;
    };

    const sortedConsolidatedData = React.useMemo(() => {
        let sorted = [...consolidatedData];
        if (consolidatedSort.key) {
            sorted.sort((a, b) => {
                let aVal = a[consolidatedSort.key];
                let bVal = b[consolidatedSort.key];
                if (typeof aVal === 'number' && typeof bVal === 'number') return consolidatedSort.direction === 'asc' ? aVal - bVal : bVal - aVal;
                if (aVal < bVal) return consolidatedSort.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return consolidatedSort.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sorted;
    }, [consolidatedData, consolidatedSort]);

    const totalConsolidatedPages = Math.ceil(sortedConsolidatedData.length / consolidatedLimit);
    const paginatedConsolidatedData = sortedConsolidatedData.slice(
        (consolidatedPage - 1) * consolidatedLimit,
        consolidatedPage * consolidatedLimit
    );

    const getConsolidatedPageNumbers = () => {
        const pages = [];
        let start = Math.max(1, consolidatedPage - 2);
        let end = Math.min(totalConsolidatedPages, start + 4);
        if (end - start < 4) start = Math.max(1, end - 4);
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    };

    // Report Date Range Handlers
    const handleReportDateChange = (field, value) => {
        setReportFilters(prev => ({ ...prev, [field]: value }));
        setRPage(1);
        setConsolidatedPage(1);
    };


    const shiftReportRange = (direction) => {
        // Parse the date components manually to avoid UTC initialization issues
        const currentStartParts = reportFilters.startDate.split('-').map(Number);
        const start = new Date(currentStartParts[0], currentStartParts[1] - 1, 1);

        if (direction === 'prev') {
            start.setMonth(start.getMonth() - 1);
        } else {
            start.setMonth(start.getMonth() + 1);
        }
        start.setDate(1);

        const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);

        setReportFilters({
            startDate: toInputDate(start),
            endDate: toInputDate(end)
        });
        setConsolidatedPage(1);
    };

    // 2. REPORT VIEW CONTENT
    const ReportViewContent = (
        <div className="space-y-6 animate-fade-in-up">
            {/* ADVANCED HR SUMMARY CARDS */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <SummaryCard
                    title="Total Employees"
                    value={consolidatedSummary?.total_employees || 0}
                    label="Total Employees"
                    icon={FiSearch}
                    colorClass="text-red-600"
                    bgColor="bg-red-50"
                />
                <SummaryCard
                    title="Working Days(This Month)"
                    value={consolidatedSummary?.working_days || 0}
                    label="Working Days"
                    icon={FiCalendar}
                    colorClass="text-orange-600"
                    bgColor="bg-orange-50"
                />
                <SummaryCard
                    title="Avg Attendance"
                    value={`${consolidatedSummary?.avg_percentage || 0}%`}
                    label="Avg Attendance"
                    icon={FiTrendingUp}
                    colorClass="text-emerald-600"
                    bgColor="bg-emerald-50"
                />
                <SummaryCard
                    title="Total Absents"
                    value={consolidatedSummary?.total_absents || 0}
                    label="Total Absents"
                    icon={FiAlertCircle}
                    colorClass="text-amber-600"
                    bgColor="bg-amber-50"
                />
            </div>

            {/* CONSOLIDATED MASTER REPORT */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                {/* Enterprise Toolbar */}
                <div className="p-6 border-b border-gray-100 bg-gray-50/30 flex flex-col xl:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-white shadow-sm rounded-xl border border-gray-200">
                            <FiFileText className="text-red-600" size={24} />
                        </div>
                        <div>
                            <h1 className="text-xl font-black text-gray-800 tracking-tight">Enterprise Attendance Report</h1>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className="flex items-center gap-1 text-[10px] font-black bg-blue-100 text-blue-700 px-2 py-0.5 rounded uppercase">Consolidated</span>
                                <span className="text-xs text-red-600 font-black tracking-tight uppercase">
                                    {reportFilters.startDate === reportFilters.endDate ?
                                        formatDateDDMMYYYY(reportFilters.startDate) :
                                        `${formatDateDDMMYYYY(reportFilters.startDate)} — ${formatDateDDMMYYYY(reportFilters.endDate)}`}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* High-End Filters */}
                    {/* High-End Filters - Responsive Layout */}
                    <div className="flex flex-wrap items-center justify-center xl:justify-end gap-3 w-full xl:w-auto">

                        {/* Entries & Date Group */}
                        <div className="flex flex-wrap items-center justify-center gap-3">
                            <div className="flex items-center gap-2 bg-white border border-gray-200 p-2 rounded-xl shadow-sm hidden sm:flex">
                                <div className="relative">
                                    <select
                                        value={consolidatedLimit}
                                        onChange={(e) => {
                                            setConsolidatedLimit(Number(e.target.value));
                                            setConsolidatedPage(1);
                                        }}
                                        className="border-none bg-transparent text-xs font-black text-gray-700 focus:ring-0 pl-1 pr-6 py-0 appearance-none cursor-pointer"
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value={999999}>All</option>
                                    </select>
                                    {/* <FiChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={14} /> */}
                                </div>
                                <span className="text-[10px] font-bold text-gray-500 uppercase">Entries</span>
                            </div>

                            <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-gray-200 shadow-sm">
                                <button onClick={() => shiftReportRange('prev')} className="p-1.5 hover:bg-gray-50 rounded-lg transition-all text-gray-500" title="Previous Range"><FiChevronLeft size={14} /></button>
                                <input
                                    type="date"
                                    value={reportFilters.startDate}
                                    onChange={e => handleReportDateChange('startDate', e.target.value)}
                                    className="border-none bg-transparent text-xs font-bold w-24 sm:w-28 focus:ring-0 p-1 text-center"
                                />
                                <span className="text-gray-300 font-black px-0.5 text-[10px]">TO</span>
                                <input
                                    type="date"
                                    value={reportFilters.endDate}
                                    onChange={e => handleReportDateChange('endDate', e.target.value)}
                                    className="border-none bg-transparent text-xs font-bold w-24 sm:w-28 focus:ring-0 p-1 text-center"
                                />
                                <button onClick={() => shiftReportRange('next')} className="p-1.5 hover:bg-gray-50 rounded-lg transition-all text-gray-500" title="Next Range"><FiChevronRight size={14} /></button>
                            </div>
                        </div>

                        {/* Filters & Actions Group */}
                        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 flex-1 xl:flex-none">
                            <select
                                value={gradeFilter}
                                onChange={e => setGradeFilter(e.target.value)}
                                className="bg-white border border-gray-200 py-2 pl-3 pr-8 rounded-xl text-xs font-bold text-gray-700 shadow-sm focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
                            >
                                <option value="All">All Grades</option>
                                <option value="Excellent">Excellent</option>
                                <option value="Good">Good</option>
                                <option value="Average">Average</option>
                                <option value="Poor">Poor</option>
                            </select>

                            <div className="relative flex-1 min-w-[140px] max-w-[200px]">
                                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={rSearch}
                                    onChange={e => setRSearch(e.target.value)}
                                    className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-blue-500 w-full bg-white shadow-sm font-bold placeholder-gray-400"
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <button onClick={() => handleExport('EXCEL')} className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-xl text-[10px] font-black uppercase tracking-widest border border-emerald-100 hover:bg-emerald-100 transition-all shadow-sm">
                                    <FiDownload size={12} /> <span className="hidden sm:inline">Excel</span>
                                </button>
                                <button onClick={() => handleExport('PDF')} className="flex items-center gap-1.5 px-3 py-2 bg-rose-50 text-rose-700 rounded-xl text-[10px] font-black uppercase tracking-widest border border-rose-100 hover:bg-rose-100 transition-all shadow-sm">
                                    <FiFileText size={12} /> <span className="hidden sm:inline">PDF</span>
                                </button>
                                <button onClick={handleSalaryExport} className="flex items-center gap-1.5 px-3 py-2 bg-violet-50 text-violet-700 rounded-xl text-[10px] font-black uppercase tracking-widest border border-violet-100 hover:bg-violet-100 transition-all shadow-sm" title="Download Salary Report Excel">
                                    <FiDownload size={12} /> <span className="hidden sm:inline">Salary Excel</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left border-collapse">
                        <thead className="bg-gray-100 text-gray-700 sticky top-0 z-20 border-b border-gray-200">
                            <tr className="divide-x divide-gray-200">
                                <th className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center w-16">No.</th>
                                <th onClick={() => requestSort('name')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest min-w-[200px] cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Employee Details <SortIcon column="name" />
                                </th>
                                <th onClick={() => requestSort('working_days')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Work Days <SortIcon column="working_days" />
                                </th>
                                <th onClick={() => requestSort('accrued_leave')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Accrued <SortIcon column="accrued_leave" />
                                </th>
                                <th onClick={() => requestSort('present')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Present <SortIcon column="present" />
                                </th>
                                <th onClick={() => requestSort('late')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Late <SortIcon column="late" />
                                </th>
                                <th onClick={() => requestSort('lop')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    LOP <SortIcon column="lop" />
                                </th>
                                <th onClick={() => requestSort('leave')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Leave <SortIcon column="leave" />
                                </th>
                                <th onClick={() => requestSort('permission')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Perm <SortIcon column="permission" />
                                </th>
                                <th onClick={() => requestSort('od')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    OD <SortIcon column="od" />
                                </th>
                                <th onClick={() => requestSort('absent')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Absent <SortIcon column="absent" />
                                </th>
                                <th onClick={() => requestSort('missing_status')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Missing Status<SortIcon column="missing_status" />
                                </th>
                                <th onClick={() => requestSort('percentage')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Atten % <SortIcon column="percentage" />
                                </th>
                                <th onClick={() => requestSort('grade')} className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center cursor-pointer hover:bg-gray-200 transition-colors group">
                                    Grade <SortIcon column="grade" />
                                </th>
                                <th className="px-6 py-4 font-black uppercase text-[10px] tracking-widest text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {consolidatedLoading ? (
                                <tr><td colSpan="15" className="text-center py-20 text-gray-400 font-bold italic animate-pulse">Running architectural aggregation...</td></tr>
                            ) : sortedConsolidatedData.length === 0 ? (
                                <tr><td colSpan="15" className="text-center py-20 text-gray-400 font-bold uppercase tracking-widest">No consolidated data found</td></tr>
                            ) : paginatedConsolidatedData.map((row, i) => (
                                <tr key={i} className={`hover:bg-[#C30010]/5 transition-all group ${row.has_lop ? 'bg-red-50/30' : ''}`}>
                                    <td className="px-6 py-5 text-center text-gray-400 font-mono text-xs">{(consolidatedPage - 1) * consolidatedLimit + i + 1}</td>
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-3">
                                            {row.img ? <img src={row.img} alt="" className="w-10 h-10 rounded-xl object-cover border-2 border-white shadow-md" /> :
                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-800 to-gray-600 text-white flex items-center justify-center font-black text-xs shadow-md">{row.name.charAt(0)}</div>}
                                            <div>
                                                <p className="font-black text-gray-800 text-sm whitespace-nowrap">{row.name}</p>
                                                <p className="text-[10px] text-red-600 font-black uppercase tracking-tight">{row.designation}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        <span className="font-bold text-gray-800">{row.working_days}</span>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        {isHROrAdmin ? (
                                            <button
                                                onDoubleClick={() => setAccruedModal({
                                                    open: true,
                                                    user_id: row.user_id,
                                                    name: row.name,
                                                    designation: row.designation,
                                                    img: row.img,
                                                    value: Math.max(0, row.accrued_leave || 0),
                                                    remarks: '',
                                                    loading: false,
                                                    error: null
                                                })}
                                                className="text-blue-700 font-black bg-blue-50 px-2.5 py-1 rounded text-xs border border-blue-100 hover:bg-blue-100 hover:border-blue-200 transition-colors cursor-pointer shadow-sm select-none inline-block"
                                                title="Double-click to update Accrued Leave Balance"
                                            >
                                                <span>{Math.max(0, row.accrued_leave || 0)}</span>
                                            </button>
                                        ) : (
                                            <span className="text-blue-700 font-black bg-blue-50 px-2.5 py-1 rounded text-xs border border-blue-100 inline-block">{Math.max(0, row.accrued_leave || 0)}</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        <div className="flex flex-col items-center">
                                            <span className="text-emerald-700 font-black bg-emerald-50 px-2.5 py-1 rounded text-xs border border-emerald-100">{row.present}</span>
                                            {row.holiday_worked > 0 && (
                                                <button
                                                    onClick={() => handleStatusClick(row.user_id, row.name, 'HOLIDAY_WORKED')}
                                                    className="text-[10px] text-purple-600 font-bold mt-1 bg-purple-50 px-1.5 rounded border border-purple-100 cursor-pointer hover:bg-purple-100 transition-colors"
                                                >
                                                    +{row.holiday_worked} OT
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        {row.late > 0 ? (
                                            <button
                                                onClick={() => handleStatusClick(row.user_id, row.name, 'LATE')}
                                                className="text-amber-700 font-black bg-amber-50 px-2.5 py-1 rounded text-xs border border-amber-100 cursor-pointer hover:bg-amber-100 transition-colors shadow-sm"
                                            >
                                                {row.late}
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 font-bold">0</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        {row.lop > 0 ? (
                                            <button
                                                onClick={() => handleStatusClick(row.user_id, row.name, 'LOP')}
                                                className="text-red-700 font-black bg-red-100 px-2.5 py-1 rounded text-xs border-2 border-red-200 cursor-pointer hover:bg-red-200 transition-colors shadow-lg shadow-red-100"
                                            >
                                                {row.lop}
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 font-bold">0</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        {row.leave > 0 ? (
                                            <button
                                                onClick={() => handleStatusClick(row.user_id, row.name, 'ON_LEAVE')}
                                                className="text-orange-800 font-black bg-orange-100 px-2.5 py-1 rounded text-xs border border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors shadow-sm"
                                            >
                                                {row.leave}
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 font-bold">0</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        {row.permission > 0 ? (
                                            <button
                                                onClick={() => handleStatusClick(row.user_id, row.name, 'PERMISSION')}
                                                className="text-indigo-700 font-black bg-indigo-50 px-2.5 py-1 rounded text-xs border border-indigo-100 cursor-pointer hover:bg-indigo-100 transition-colors shadow-sm"
                                            >
                                                {row.permission}
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 font-bold">0</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        {row.od > 0 ? (
                                            <button
                                                onClick={() => handleStatusClick(row.user_id, row.name, 'OD')}
                                                className="text-purple-700 font-black bg-purple-50 px-2.5 py-1 rounded text-xs border border-purple-100 cursor-pointer hover:bg-purple-100 transition-colors shadow-sm"
                                            >
                                                {row.od}
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 font-bold">0</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        {row.absent > 0 ? (
                                            <button
                                                onClick={() => handleStatusClick(row.user_id, row.name, 'ABSENT')}
                                                className="text-rose-700 font-black bg-rose-50 px-2.5 py-1 rounded text-xs border border-rose-100 cursor-pointer hover:bg-rose-100 transition-colors shadow-sm"
                                            >
                                                {row.absent}
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 font-bold">0</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        {row.missing_status > 0 ? (
                                            <button
                                                onClick={() => handleStatusClick(row.user_id, row.name, 'MISSING_STATUS')}
                                                className="text-orange-700 font-black bg-orange-50 px-2.5 py-1 rounded text-xs border border-orange-100 cursor-pointer hover:bg-orange-100 transition-colors shadow-sm"
                                            >
                                                {row.missing_status}
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 font-bold">0</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        <div className="flex flex-col items-center gap-1">
                                            <span className="font-black text-gray-800 text-sm leading-none">{row.percentage}%</span>
                                            <div className="w-16 bg-gray-100 h-1.5 rounded-full overflow-hidden border border-gray-200">
                                                <div className={`h-full ${parseFloat(row.percentage) >= 90 ? 'bg-emerald-500' : parseFloat(row.percentage) >= 80 ? 'bg-blue-500' : parseFloat(row.percentage) >= 70 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${row.percentage}%` }}></div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        <span className={`px-2.5 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest border-2 ${row.grade_variant}`}>
                                            {row.grade}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        <button
                                            onClick={() => {
                                                setSelectedEmp({ id: row.user_id, name: row.name });
                                                setRPage(1); // Reset page for detailed logs
                                            }}
                                            className="px-4 py-2 bg-white text-[#C30010] border-2 border-[#C30010] rounded-xl text-[11px] font-black uppercase tracking-tight hover:bg-[#C30010] hover:text-white transition-all shadow-sm active:scale-95 whitespace-nowrap"
                                        >
                                            View Details
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>


                <div className="p-4 bg-gray-50 border-t border-gray-100">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        {/* Left: Showing info */}
                        <div className="text-sm text-gray-600 font-medium">
                            Showing {Math.min((consolidatedPage - 1) * consolidatedLimit + 1, sortedConsolidatedData.length)} to {Math.min(consolidatedPage * consolidatedLimit, sortedConsolidatedData.length)} of {sortedConsolidatedData.length} entries
                        </div>

                        {/* Right: Page navigation */}
                        {totalConsolidatedPages > 1 && (
                            <div className="flex items-center gap-2 sm:gap-3">
                                <button
                                    disabled={consolidatedPage === 1}
                                    onClick={() => setConsolidatedPage(p => p - 1)}
                                    className="w-9 h-9 flex items-center justify-center text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <FiChevronLeft size={16} />
                                </button>
                                <div className="flex gap-1.5">
                                    {getConsolidatedPageNumbers().map(p => (
                                        <button
                                            key={p}
                                            onClick={() => setConsolidatedPage(p)}
                                            className={`w-9 h-9 flex items-center justify-center text-xs font-black rounded-xl transition-all ${consolidatedPage === p
                                                ? 'bg-[#C30010] text-white shadow-lg shadow-red-100 ring-4 ring-red-50'
                                                : 'text-gray-900 font-bold hover:bg-gray-100 border border-transparent'
                                                }`}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    disabled={consolidatedPage === totalConsolidatedPages}
                                    onClick={() => setConsolidatedPage(p => p + 1)}
                                    className="w-9 h-9 flex items-center justify-center text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <FiChevronRight size={16} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* MODAL / OVERLAY FOR DETAILED DRILL-DOWN */}
            {selectedEmp && (
                <div className="fixed inset-0 z-[100] bg-gray-900/60 backdrop-blur-md flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl h-4/5 overflow-hidden flex flex-col border border-white/20 animate-scale-in">
                        <div className="p-6 bg-gray-100 border-b border-gray-200 text-gray-800 flex justify-between items-center shadow-sm">
                            <div className="flex items-center gap-4">
                                <button onClick={() => setSelectedEmp(null)} className="p-2 hover:bg-white/10 rounded-xl transition-all"><FiArrowLeft size={24} /></button>
                                <div>
                                    <h2 className="text-xl font-black tracking-tight">{selectedEmp.name} - Detailed Period Logs</h2>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{formatDateDDMMYYYY(reportFilters.startDate)} — {formatDateDDMMYYYY(reportFilters.endDate)}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleExportDetailedExcel}
                                    disabled={reportLoading || !reportData || reportData.length === 0}
                                    title={reportLoading ? 'Loading employee data, please wait...' : 'Export to Excel'}
                                    className="px-6 py-2 bg-[#059669] text-white hover:bg-[#047857] rounded-full transition-all shadow-md flex items-center gap-2 font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#059669]"
                                >
                                    {reportLoading ? (
                                        <>
                                            <svg className="animate-spin" width={18} height={18} viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
                                            <span>Loading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <FiDownload size={18} />
                                            <span>Export</span>
                                        </>
                                    )}
                                </button>
                                <button onClick={() => setSelectedEmp(null)} className="p-2 bg-gray-200/50 hover:bg-gray-200 rounded-full transition-all text-gray-600 hover:text-gray-900"><FiXCircle size={28} /></button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                            {reportLoading ? (
                                <div className="h-full flex flex-col items-center justify-center space-y-4">
                                    <div className="w-12 h-12 border-4 border-[#C30010] border-t-transparent rounded-full animate-spin"></div>
                                    <p className="text-sm font-black text-gray-400 uppercase tracking-widest">Compiling Daily Logs...</p>
                                </div>
                            ) : (
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
                                    <table className="w-full text-[11px] text-left border-collapse">
                                        <thead className="bg-gray-100 text-gray-600 text-[10px] font-black uppercase tracking-widest sticky top-0 border-b border-gray-200">
                                            <tr>
                                                <th className="px-3 py-4 w-10 text-center text-gray-400">#</th>
                                                <th className="px-3 py-4 whitespace-nowrap">Date</th>
                                                <th className="px-3 py-4 text-center">In</th>
                                                <th className="px-3 py-4 text-center">Out</th>
                                                <th className="px-3 py-4 text-center">Permission</th>
                                                <th className="px-3 py-4 text-center">Deviation</th>
                                                <th className="px-3 py-4 text-center">OT</th>
                                                <th className="px-3 py-4 text-center">Production</th>
                                                <th className="px-3 py-4">Punches</th>
                                                <th className="px-3 py-4 text-center">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {reportData.map((d, idx) => (
                                                <tr key={idx} className="hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0">
                                                    <td className="px-3 py-4 text-center text-gray-400 font-mono text-[10px]">{idx + 1}</td>
                                                    <td className="px-3 py-4 font-bold text-gray-700 whitespace-nowrap">{formatDateDDMMYYYY(d.attendance_date)}</td>
                                                    <td className="px-3 py-4 text-center font-bold text-emerald-600">{d.check_in || '--:--'}</td>
                                                    <td className="px-3 py-4 text-center font-bold text-rose-600">{d.check_out || '--:--'}</td>
                                                    <td className="px-3 py-4 text-center">
                                                        {(d.permission_start_time || d.permission_end_time) ? (
                                                            <div className="flex flex-col items-center">
                                                                <span className="text-[9px] font-black text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-100 whitespace-nowrap">
                                                                    {d.permission_start_time || '--:--'} - {d.permission_end_time || '--:--'}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-300 text-[10px]">-</span>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-4 text-center font-bold text-amber-600 text-[10px]">{(d.lateVal !== undefined && d.lateVal !== null) ? d.lateVal : calculateLate(d.check_in)}</td>
                                                    <td className="px-3 py-4 text-center font-bold text-red-600 text-[10px]">{(d.otVal !== undefined && d.otVal !== null) ? d.otVal : calculateOvertime(d.check_out)}</td>
                                                    <td className="px-3 py-4 text-center">
                                                        {(() => {
                                                            const prod = calculateProduction(d.check_in, d.check_out);
                                                            return prod !== '-' ? (
                                                                <span className={`px-2 py-1 rounded-md text-[10px] font-black text-white shadow-sm flex items-center gap-1 w-fit mx-auto
                                                                    ${parseFloat(prod) >= 8 ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                                                                    <FiClock size={10} /> {prod}
                                                                </span>
                                                            ) : <span className="text-gray-300">-</span>;
                                                        })()}
                                                    </td>
                                                    <td className="px-3 py-4 text-[9px] font-mono text-gray-400 max-w-[120px] truncate" title={d.punch_records}>{d.punch_records || 'No Logs'}</td>
                                                    <td className="px-3 py-4 text-center whitespace-nowrap">
                                                        {(() => {
                                                            const hasPunches = d.check_in && d.check_in !== '--:--' && d.check_in !== '-';
                                                            let displayStatus = d.status || 'ABSENT';
                                                            if (d.status === 'LOP') {
                                                                displayStatus = hasPunches ? 'LATE (LOP)' : 'ABSENT';
                                                            } else if (d.status === 'ABSENT' && hasPunches) {
                                                                displayStatus = 'LATE';
                                                            }
                                                            return (
                                                                <span className={`px-2 py-1 rounded-[4px] text-[9px] font-black uppercase tracking-tight
                                                                    ${displayStatus === 'PRESENT' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' :
                                                                        (displayStatus === 'ABSENT' || displayStatus === 'NOT_MARKED') ? 'bg-rose-100 text-rose-700 border border-rose-200' :
                                                                            displayStatus === 'LATE' ? 'bg-amber-100 text-amber-700 border border-amber-200' :
                                                                                displayStatus.includes('LOP') ? 'bg-orange-100 text-orange-700 border border-orange-200' :
                                                                                    (displayStatus === 'HALF_DAY' || displayStatus === 'HALF_DAY_LEAVE') ? 'bg-orange-100 text-orange-700 border border-orange-200' :
                                                                                        (displayStatus === 'ON_LEAVE' || (displayStatus && displayStatus.includes('LEAVE'))) ? 'bg-purple-100 text-purple-700 border border-purple-200' :
                                                                                            displayStatus === 'PERMISSION' ? 'bg-pink-100 text-pink-700 border border-pink-200' :
                                                                                                (displayStatus === 'AUTO PERM' || displayStatus === 'AUTO_PERM') ? 'bg-violet-100 text-violet-700 border border-violet-200' :
                                                                                                    displayStatus === 'ON_DUTY' ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' :
                                                                                                        'bg-gray-100 text-gray-600 border border-gray-200'}`}>
                                                                    {displayStatus}
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* STATUS DATES POPUP */}
            {statusDatesPopup && (
                <div className="fixed inset-0 z-[110] bg-gray-900/60 backdrop-blur-md flex items-center justify-center p-4 sm:p-6">
                    <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-3xl h-[80vh] flex flex-col border border-white/20 animate-scale-in overflow-hidden">
                        {/* Header */}
                        <div className="p-4 sm:p-6 bg-gradient-to-r from-[#C30010] to-[#A00008] text-white flex justify-between items-start shadow-lg shrink-0">
                            <div>
                                <h2 className="text-lg sm:text-xl font-black tracking-tight">{statusDatesPopup.userName}</h2>
                                <p className="text-[10px] sm:text-xs font-bold opacity-90 uppercase tracking-widest mt-1">
                                    {statusDatesPopup.status.replace('_', ' ')} Days - {statusDatesPopup.dates?.length || 0} Total
                                </p>
                                {/* Month Summary */}
                                {statusDatesPopup.dates?.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {Object.entries((statusDatesPopup.dates || []).reduce((acc, d) => {
                                            const m = new Date(d.date).toLocaleString('default', { month: 'short' });
                                            acc[m] = (acc[m] || 0) + 1;
                                            return acc;
                                        }, {})).map(([m, c]) => (
                                            <span key={m} className="px-2 py-0.5 bg-white/20 rounded text-[10px] font-bold backdrop-blur-sm">
                                                {m}: {c}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => setStatusDatesPopup(null)}
                                className="p-1.5 hover:bg-white/20 rounded-xl transition-all"
                            >
                                <FiXCircle size={24} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50/50">
                            {statusDatesLoading ? (
                                <div className="h-full flex flex-col items-center justify-center space-y-4">
                                    <div className="w-12 h-12 border-4 border-[#C30010] border-t-transparent rounded-full animate-spin"></div>
                                    <p className="text-sm font-black text-gray-400 uppercase tracking-widest">Loading Dates...</p>
                                </div>
                            ) : statusDatesPopup.dates?.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center space-y-4">
                                    <FiAlertCircle size={48} className="text-gray-300" />
                                    <p className="text-sm font-black text-gray-400 uppercase tracking-widest">No {statusDatesPopup.status} Days Found</p>
                                </div>
                            ) : (
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm text-left min-w-[600px]">
                                            <thead className="bg-gray-100 text-gray-700 text-[10px] font-black uppercase tracking-widest sticky top-0 border-b border-gray-200">
                                                <tr>
                                                    <th className="px-4 py-3 sm:px-6 sm:py-4 w-12 text-center">#</th>
                                                    <th className="px-4 py-3 sm:px-6 sm:py-4">Date</th>
                                                    {statusDatesPopup.status === 'LATE' && (
                                                        <th className="px-4 py-3 sm:px-6 sm:py-4">Status</th>
                                                    )}
                                                    <th className="px-4 py-3 sm:px-6 sm:py-4">Check-In</th>
                                                    <th className="px-4 py-3 sm:px-6 sm:py-4">Check-Out</th>
                                                    {statusDatesPopup.status === 'PERMISSION' && (
                                                        <th className="px-4 py-3 sm:px-6 sm:py-4">Perm Duration</th>
                                                    )}
                                                    <th className="px-4 py-3 sm:px-6 sm:py-4">
                                                        {statusDatesPopup.status === 'PERMISSION' ? 'Reason' : 'Punch Records'}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {statusDatesPopup.dates.map((d, idx) => {
                                                    const isAutoPerm = d.status === 'AUTO PERM';
                                                    const isPermission = d.status === 'PERMISSION';
                                                    return (
                                                        <tr key={idx} className={`transition-colors ${isAutoPerm ? 'bg-orange-50/60 hover:bg-orange-50' : isPermission ? 'bg-pink-50/40 hover:bg-pink-50/60' : 'hover:bg-[#C30010]/5'}`}>
                                                            <td className="px-4 py-3 sm:px-6 sm:py-4 text-center text-gray-400 font-mono text-xs">{idx + 1}</td>
                                                            <td className="px-4 py-3 sm:px-6 sm:py-4 font-black text-gray-700 whitespace-nowrap">{formatDateDDMMYYYY(d.date)}</td>
                                                            {statusDatesPopup.status === 'LATE' && (
                                                                <td className="px-4 py-3 sm:px-6 sm:py-4 whitespace-nowrap">
                                                                    {isAutoPerm ? (
                                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black bg-orange-100 text-orange-700 uppercase tracking-wide border border-orange-200">
                                                                            Auto Perm
                                                                        </span>
                                                                    ) : isPermission ? (
                                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black bg-pink-100 text-pink-700 uppercase tracking-wide border border-pink-200">
                                                                            Permission
                                                                        </span>
                                                                    ) : (
                                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black bg-red-100 text-red-700 uppercase tracking-wide border border-red-200">
                                                                            Late
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            )}
                                                            <td className="px-4 py-3 sm:px-6 sm:py-4 font-bold text-emerald-600 whitespace-nowrap">{d.check_in}</td>
                                                            <td className="px-4 py-3 sm:px-6 sm:py-4 font-bold text-rose-600 whitespace-nowrap">{d.check_out}</td>
                                                            {statusDatesPopup.status === 'PERMISSION' && (
                                                                <td className="px-4 py-3 sm:px-6 sm:py-4 font-bold text-indigo-600 whitespace-nowrap">{d.permission_time || '-'}</td>
                                                            )}
                                                            <td className="px-4 py-3 sm:px-6 sm:py-4 text-xs text-gray-500 font-mono min-w-[200px]">{d.punch_records}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 bg-gray-100 border-t border-gray-200 flex justify-between items-center shrink-0">
                            <span className="text-xs text-gray-500 font-medium">
                                Period: {formatDateDDMMYYYY(reportFilters.startDate)} - {formatDateDDMMYYYY(reportFilters.endDate)}
                            </span>
                            <button
                                onClick={() => setStatusDatesPopup(null)}
                                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl text-xs font-black uppercase tracking-tight transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    const handleSaveShift = async (e) => {
        e.preventDefault();
        const form = e.target;
        const payload = {
            id: editingShift?.id || null,
            shift_name: form.shift_name.value,
            start_time: form.start_time.value,
            end_time: form.end_time.value,
            late_threshold: form.late_threshold.value,
            permission_threshold: form.permission_threshold.value,
            monthly_permission_limit: parseInt(form.monthly_permission_limit.value || 3, 10),
            half_day_split_time: form.half_day_split_time.value,
            grace_period: parseInt(form.grace_period.value || 0, 10),
            auto_permission: form.auto_permission.checked ? 1 : 0,
            went_late_grace_time: form.went_late_grace_time.value,
            status: editingShift ? (editingShift.status ?? 1) : 1
        };

        const loadToast = toast.loading(editingShift ? 'Updating shift...' : 'Creating shift...');
        try {
            const res = await api.post('/attendance/save-shift', payload);
            if (res.data.status) {
                toast.success(res.data.message || 'Shift saved successfully', { id: loadToast });
                setShowShiftForm(false);
                setEditingShift(null);
                fetchShifts();
            }
        } catch (e) {
            toast.error(e.response?.data?.messages?.error || 'Failed to save shift', { id: loadToast });
        }
    };

    const handleAssignShift = async (e) => {
        e.preventDefault();
        if (!selectedEmployeeForShift) return;
        if (!selectedShiftIdToAssign) {
            toast.error('Please select a shift to assign');
            return;
        }

        const payload = {
            user_id: selectedEmployeeForShift.user_id,
            shift_id: selectedShiftIdToAssign,
            effective_date: shiftFormDate,
            end_date: shiftFormEndDate || null,
            reason: shiftFormReason
        };

        const loadToast = toast.loading('Assigning shift...');
        try {
            const res = await api.post('/attendance/assign-shift', payload);
            if (res.data.status) {
                toast.success(res.data.message || 'Shift assigned successfully', { id: loadToast });
                setAssigningShiftToEmployee(false);
                setShiftFormReason('New shift assignment');
                setShiftFormEndDate('');
                fetchEmployeeShiftsHistory(selectedEmployeeForShift.user_id);
            }
        } catch (e) {
            toast.error(e.response?.data?.messages?.error || 'Failed to assign shift', { id: loadToast });
        }
    };

    const handleSetDefaultShift = async (id, effectiveDate) => {
        const loadToast = toast.loading('Setting fallback default shift...');
        try {
            const res = await api.post('/attendance/save-shift', {
                id,
                is_default: 1,
                effective_date: effectiveDate
            });
            if (res.data.status) {
                toast.success(res.data.message || 'Default shift updated successfully', { id: loadToast });
                setDefaultShiftTarget(null);
                fetchShifts();
            }
        } catch (e) {
            toast.error(e.response?.data?.messages?.error || 'Failed to update default shift', { id: loadToast });
        }
    };

    const handleDeleteShift = (id) => {
        const s = shifts.find(item => item.id === id);
        const shiftName = s ? s.shift_name : `Shift #${id}`;
        setDeleteTarget({
            type: 'shift',
            id: id,
            title: 'Delete Shift Template?',
            desc: `Are you sure you want to delete the shift "${shiftName}"? All user assignments referencing it will be automatically deleted!`,
        });
    };

    const handleDeleteEmployeeShift = (id) => {
        setDeleteTarget({
            type: 'assignment',
            id: id,
            title: 'Delete Shift Assignment?',
            desc: 'Are you sure you want to delete this shift assignment? The timeline log and active ranges will be automatically re-aligned.',
        });
    };

    const executeDeleteConfirmed = async () => {
        if (!deleteTarget) return;
        const { type, id } = deleteTarget;
        setDeleteTarget(null);

        if (type === 'shift') {
            const loadToast = toast.loading('Deleting shift template...');
            try {
                const res = await api.post(`/attendance/delete-shift/${id}`);
                if (res.data.status) {
                    toast.success(res.data.message || 'Shift template deleted successfully', { id: loadToast });
                    fetchShifts();
                }
            } catch (e) {
                toast.error(e.response?.data?.messages?.error || 'Failed to delete shift template', { id: loadToast });
            }
        } else if (type === 'assignment') {
            const loadToast = toast.loading('Deleting shift assignment...');
            try {
                const res = await api.post(`/attendance/delete-employee-shift/${id}`);
                if (res.data.status) {
                    toast.success(res.data.message || 'Shift assignment deleted', { id: loadToast });
                    fetchEmployeeShiftsHistory(selectedEmployeeForShift.user_id);
                }
            } catch (e) {
                toast.error(e.response?.data?.messages?.error || 'Failed to delete shift assignment', { id: loadToast });
            }
        }
    };

    const handleUpdateEmployeeShift = async (e) => {
        e.preventDefault();
        if (!editingEmployeeShift) return;

        const payload = {
            id: editingEmployeeShift.id,
            shift_id: selectedShiftIdToAssign,
            effective_date: shiftFormDate,
            end_date: shiftFormEndDate || null,
            reason: shiftFormReason
        };

        const loadToast = toast.loading('Updating shift assignment...');
        try {
            const res = await api.post('/attendance/update-employee-shift', payload);
            if (res.data.status) {
                toast.success(res.data.message || 'Shift assignment updated successfully', { id: loadToast });
                setEditingEmployeeShift(null);
                setAssigningShiftToEmployee(false);
                setShiftFormReason('New shift assignment');
                setShiftFormEndDate('');
                fetchEmployeeShiftsHistory(selectedEmployeeForShift.user_id);
            }
        } catch (e) {
            toast.error(e.response?.data?.messages?.error || 'Failed to update shift assignment', { id: loadToast });
        }
    };



    return (
        <div className="space-y-6 animate-fade-in-up pb-10">
            {deleteTarget && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm border border-gray-150 overflow-hidden p-6 text-center"
                    >
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-red-50 rounded-full mb-4">
                            <FiTrash2 className="text-[#C30010]" size={20} />
                        </div>
                        <h3 className="text-gray-800 font-extrabold text-base mb-2">
                            {deleteTarget.title}
                        </h3>
                        <p className="text-gray-500 text-xs leading-relaxed mb-6 px-2">
                            {deleteTarget.desc}
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setDeleteTarget(null)}
                                className="flex-1 py-2 border border-gray-200 text-gray-600 rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-gray-50 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={executeDeleteConfirmed}
                                className="flex-1 py-2 bg-gradient-to-r from-[#C30010] to-[#99000a] text-white rounded-xl text-xs font-bold uppercase tracking-wider hover:opacity-95 shadow-md transition-all"
                            >
                                Delete
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            {defaultShiftTarget && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm border border-gray-150 overflow-hidden p-6 text-center"
                    >
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-amber-50 rounded-full mb-4">
                            <FiStar className="text-amber-500" size={20} fill="currentColor" />
                        </div>
                        <h3 className="text-gray-800 font-extrabold text-base mb-2">
                            {defaultShiftTarget.is_current ? 'Edit Default Shift Date?' : 'Set Default Shift Fallback?'}
                        </h3>
                        <p className="text-gray-500 text-xs leading-relaxed mb-4 px-2">
                            {defaultShiftTarget.is_current ? (
                                <>
                                    You are editing the effective start date for the default fallback shift <b>{defaultShiftTarget.shift_name}</b>.
                                    Select the new date from which this timing configuration should apply.
                                </>
                            ) : (
                                <>
                                    Set <b>{defaultShiftTarget.shift_name}</b> as the default fallback shift for all employees.
                                    Select from which date this timing change should take effect to protect historical logs.
                                </>
                            )}
                        </p>

                        <div className="mb-6 max-w-[200px] mx-auto">
                            <label className="block text-[9px] font-black uppercase tracking-wider text-gray-500 mb-1">Effective Start Date</label>
                            <div className="flex items-center bg-white rounded-lg p-0.5 border border-gray-250">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const d = new Date(defaultShiftEffectiveDate); d.setDate(d.getDate() - 1);
                                        setDefaultShiftEffectiveDate(d.toISOString().split('T')[0]);
                                    }}
                                    className="p-1 hover:bg-gray-100 rounded text-gray-600"
                                >
                                    <FiChevronLeft size={14} />
                                </button>
                                <input
                                    required
                                    type="date"
                                    value={defaultShiftEffectiveDate}
                                    onChange={(e) => setDefaultShiftEffectiveDate(e.target.value)}
                                    className="bg-transparent border-none text-xs font-semibold text-gray-700 focus:ring-0 p-1 w-full text-center font-mono"
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        const d = new Date(defaultShiftEffectiveDate); d.setDate(d.getDate() + 1);
                                        setDefaultShiftEffectiveDate(d.toISOString().split('T')[0]);
                                    }}
                                    className="p-1 hover:bg-gray-100 rounded text-gray-600"
                                >
                                    <FiChevronRight size={14} />
                                </button>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setDefaultShiftTarget(null);
                                    setDefaultShiftEffectiveDate(new Date().toISOString().split('T')[0]);
                                }}
                                className="flex-1 py-2 border border-gray-200 text-gray-600 rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-gray-50 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleSetDefaultShift(defaultShiftTarget.id, defaultShiftEffectiveDate)}
                                className="flex-1 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl text-xs font-bold uppercase tracking-wider hover:opacity-95 shadow-md transition-all"
                            >
                                Confirm
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            {isAssigningBulk && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 15 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 15 }}
                        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-100 flex flex-col max-h-[90vh]"
                    >
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-red-700 to-[#C30010] p-6 text-white shrink-0 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/10 p-2 rounded-xl backdrop-blur-md">
                                    <FiUsers size={20} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-base font-black uppercase tracking-wider">Assign Shift in Bulk</h3>
                                    <p className="text-[10px] text-red-100 font-bold uppercase tracking-tight mt-0.5">Assign shift templates to multiple employees</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setIsAssigningBulk(false);
                                    setSelectedEmployeesForBulk([]);
                                }}
                                className="p-1 hover:bg-white/10 rounded-lg transition-colors text-white"
                            >
                                <FiXCircle size={24} />
                            </button>
                        </div>

                        {/* Modal Body / Form */}
                        <form onSubmit={handleAssignShiftBulkSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
                            {/* Select Employees */}
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2">Select Employees</label>
                                <Select
                                    isMulti
                                    options={employeeOptions}
                                    value={selectedEmployeesForBulk}
                                    onChange={setSelectedEmployeesForBulk}
                                    placeholder="Search and select employees..."
                                    className="react-select-container text-xs font-semibold"
                                    classNamePrefix="react-select"
                                />
                            </div>

                            {/* Select Shift Template */}
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2">Select Shift Template</label>
                                <select
                                    required
                                    value={bulkSelectedShiftId}
                                    onChange={(e) => setBulkSelectedShiftId(e.target.value)}
                                    className="w-full px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-xs focus:outline-none focus:border-[#C30010] font-semibold"
                                >
                                    <option value="" disabled>Choose a shift...</option>
                                    {shifts.map(s => (
                                        <option key={s.id} value={s.id}>{s.shift_name} ({s.start_time.slice(0, 5)} - {s.end_time.slice(0, 5)})</option>
                                    ))}
                                </select>
                            </div>

                            {/* Dates */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {/* Effective Date */}
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2">Effective Date</label>
                                    <input
                                        required
                                        type="date"
                                        value={bulkShiftFormDate}
                                        onChange={(e) => setBulkShiftFormDate(e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-50 rounded-xl border border-gray-200 text-xs focus:outline-none focus:border-[#C30010] font-semibold font-mono"
                                    />
                                </div>

                                {/* End Date */}
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2">End Date (Optional)</label>
                                    <input
                                        type="date"
                                        value={bulkShiftFormEndDate}
                                        onChange={(e) => setBulkShiftFormEndDate(e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-50 rounded-xl border border-gray-200 text-xs focus:outline-none focus:border-[#C30010] font-semibold font-mono"
                                    />
                                </div>
                            </div>

                            {/* Reason / Memo */}
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2">Reason</label>
                                <input
                                    type="text"
                                    value={bulkShiftFormReason}
                                    onChange={(e) => setBulkShiftFormReason(e.target.value)}
                                    placeholder="e.g. Bulk team roster change"
                                    className="w-full px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-xs focus:outline-none focus:border-[#C30010] font-semibold"
                                />
                            </div>

                            {/* Footer Action Buttons */}
                            <div className="flex gap-3 pt-4 border-t border-gray-100 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsAssigningBulk(false);
                                        setSelectedEmployeesForBulk([]);
                                    }}
                                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 font-black rounded-xl hover:bg-gray-200 transition text-xs uppercase tracking-wider"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isBulkSubmitting}
                                    className="flex-1 px-4 py-2.5 bg-[#C30010] text-white font-black rounded-xl hover:bg-red-800 shadow-md hover:shadow-lg transition text-xs uppercase tracking-wider disabled:opacity-50"
                                >
                                    {isBulkSubmitting ? 'Processing...' : 'Confirm Assignment'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}


            {/* Top Header & Re-calculate */}
            <div className="flex flex-col md:flex-row justify-between items-center bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">Attendance Desk</h2>
                    <p className="text-xs text-gray-500 mt-1">Manage employee attendance, reports, and logs.</p>
                </div>
                <div>
                    {/* Re-calculate button kept as requested */}
                    <button
                        onClick={() => handleRecalculate()}
                        disabled={isRecalculating}
                        className="flex items-center gap-2 px-4 py-2 bg-red-50 text-[#C30010] rounded-lg font-medium hover:bg-red-100 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <FiRefreshCw className={isRecalculating ? "animate-spin" : ""} /> {isRecalculating ? 'Updating...' : 'Fetch & Update'}
                    </button>
                </div>
            </div>

            {/* Submenu / Tab Navigation */}
            <div className="flex flex-wrap items-center gap-4 md:gap-8 border-b border-gray-200 px-2">
                <button
                    onClick={() => setCurrentView('dashboard')}
                    className={`pb-3 text-sm font-bold transition-all relative flex-shrink-0 ${currentView === 'dashboard' ? 'text-[#C30010] border-b-2 border-[#C30010]' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    Dashboard
                </button>
                <button
                    onClick={() => setCurrentView('register')}
                    className={`pb-3 text-sm font-bold transition-all relative flex-shrink-0 ${currentView === 'register' ? 'text-[#C30010] border-b-2 border-[#C30010]' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    Attendance Register
                </button>
                <button
                    onClick={() => setCurrentView('report')}
                    className={`pb-3 text-sm font-bold transition-all relative flex-shrink-0 ${currentView === 'report' ? 'text-[#C30010] border-b-2 border-[#C30010]' : 'text-gray-500 hover:text-gray-700'}`}
                >
                    Attendance Report
                </button>
                {isHROrAdmin && (
                    <button
                        onClick={() => setCurrentView('shifts')}
                        className={`pb-3 text-sm font-bold transition-all relative flex-shrink-0 ${currentView === 'shifts' ? 'text-[#C30010] border-b-2 border-[#C30010]' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Shift Management
                    </button>
                )}
            </div>


            {/* View Switching */}
            {currentView === 'dashboard' && <DashboardView />}
            {currentView === 'shifts' && isHROrAdmin && (
                <ShiftManagementView
                    shifts={shifts}
                    selectedEmployeeForShift={selectedEmployeeForShift}
                    employeeShiftsHistory={employeeShiftsHistory}
                    allEmployees={allEmployees}
                    showShiftForm={showShiftForm}
                    editingShift={editingShift}
                    assigningShiftToEmployee={assigningShiftToEmployee}
                    shiftFormDate={shiftFormDate}
                    shiftFormEndDate={shiftFormEndDate}
                    shiftFormReason={shiftFormReason}
                    selectedShiftIdToAssign={selectedShiftIdToAssign}
                    shiftsLoading={shiftsLoading}
                    editingEmployeeShift={editingEmployeeShift}
                    isHROrAdmin={isHROrAdmin}
                    handleSaveShift={handleSaveShift}
                    handleAssignShift={handleAssignShift}
                    handleDeleteShift={handleDeleteShift}
                    handleDeleteEmployeeShift={handleDeleteEmployeeShift}
                    handleSetDefaultShift={handleSetDefaultShift}
                    fetchEmployeeShiftsHistory={fetchEmployeeShiftsHistory}
                    setSelectedEmployeeForShift={setSelectedEmployeeForShift}
                    setSelectedShiftIdToAssign={setSelectedShiftIdToAssign}
                    setShiftFormDate={setShiftFormDate}
                    setShiftFormEndDate={setShiftFormEndDate}
                    setShiftFormReason={setShiftFormReason}
                    setAssigningShiftToEmployee={setAssigningShiftToEmployee}
                    setEditingEmployeeShift={setEditingEmployeeShift}
                    setShowShiftForm={setShowShiftForm}
                    setEditingShift={setEditingShift}
                    setDefaultShiftTarget={setDefaultShiftTarget}
                    setDefaultShiftEffectiveDate={setDefaultShiftEffectiveDate}
                    defaultShiftEffectiveDate={defaultShiftEffectiveDate}
                    handleUpdateEmployeeShift={handleUpdateEmployeeShift}
                    setShifts={setShifts}
                    setIsAssigningBulk={setIsAssigningBulk}
                    setSelectedEmployeesForBulk={setSelectedEmployeesForBulk}
                />
            )}
            {currentView === 'report' && ReportViewContent}
            {currentView === 'register' && (

                <RegisterView
                    registerDate={registerDate}
                    setRegisterDate={setRegisterDate}
                    handleManualClick={handleManualClick}
                    handleCancelStatus={handleCancelStatus}
                    refreshTrigger={refreshTrigger}
                    handlePunchEdit={handlePunchEdit}
                    isHROrAdmin={isHROrAdmin}
                />
            )}
            {/* Modal for Manual Entry / View Details */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="font-bold text-gray-800">
                                {(modalMode === 'EDIT' || modalMode === 'EDIT_PUNCH')
                                    ? `Mark ${modalData?.newStatus ? modalData.newStatus.replace('ON_', '') : 'Status'}`
                                    : 'Status Details'
                                }
                            </h3>
                            <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600"><FiXCircle size={24} /></button>
                        </div>

                        <div className="p-6 space-y-4">
                            {/* Common Info */}
                            <div className="flex justify-between items-center">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Employee</label>
                                    <p className="font-medium text-gray-800">{modalData?.name}</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Date</label>
                                    <p className="font-medium text-gray-800">{formatDateDDMMYYYY(modalData?.date)}</p>
                                </div>
                            </div>

                            {/* VIEW MODE CONTENT */}
                            {modalMode === 'VIEW' && (
                                <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-100">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Current Status</label>
                                            <span className={`px-2 py-1 rounded text-xs font-bold ${modalData.color || 'bg-gray-200'}`}>{modalData.status === 'LOP' ? 'LATE (LOP)' : modalData.status}</span>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Source</label>
                                            <span className="text-xs font-bold text-blue-600">
                                                {modalData.is_manual_override == 1 ? 'Manual Override' : (modalData.sched_reason ? 'Scheduled (System)' : 'Auto-Calculated')}
                                            </span>
                                        </div>
                                    </div>

                                    {(modalData.permission_start_time || modalData.permission_end_time) && (
                                        <div className="flex justify-between items-center bg-purple-50 p-2 rounded border border-purple-100">
                                            <span className="text-xs font-bold text-purple-700">Permission Time</span>
                                            <span className="text-xs font-bold text-gray-700">
                                                {modalData.permission_start_time || '--:--'} to {modalData.permission_end_time || '--:--'}
                                            </span>
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Reason / Notes</label>
                                        <div className="text-sm text-gray-700 bg-white p-2 rounded border border-gray-200">
                                            {modalData.sched_reason ? (
                                                <span className="text-purple-700 font-medium">{modalData.sched_reason}</span>
                                            ) : modalData.override_reason ? (
                                                <span className="text-orange-700 font-medium">{modalData.override_reason}</span>
                                            ) : (
                                                <span className="text-gray-400 italic">No specific reason logged.</span>
                                            )}
                                        </div>
                                    </div>
                                    {/* Edit Option */}
                                    <div className="pt-2">
                                        <button
                                            onClick={() => {
                                                setManualReason(modalData.override_reason || '');
                                                setModalData({ ...modalData, newStatus: modalData.status });
                                                setModalMode('EDIT');
                                            }}
                                            className="text-red-600 text-xs font-bold hover:underline flex items-center gap-1"
                                        >
                                            <FiEdit2 /> Edit Status / Reason
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* EDIT MODE CONTENT */}
                            {modalMode === 'EDIT' && (
                                <div className="space-y-4">
                                    {(modalData?.newStatus === 'ON_LEAVE' || modalData?.newStatus === 'HALF_DAY') && (
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                                <div>
                                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Leave Type</label>
                                                    <p className="font-bold text-gray-800 text-sm">
                                                        {modalData.newStatus === 'HALF_DAY' ? 'Half Day Leave' : 'Full Day Leave'}
                                                    </p>
                                                </div>
                                                <select
                                                    className="bg-white border border-gray-200 text-xs font-bold rounded-lg pl-3 pr-8 py-1.5 focus:ring-0 cursor-pointer shadow-sm appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20stroke%3D%22%236b7280%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22m6%208%204%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-[right_0.5rem_center] bg-no-repeat"
                                                    value={modalData.newStatus}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (val === 'HALF_DAY') {
                                                            const shiftStart = halfDayShift?.start_time?.slice(0, 5) || DEFAULT_LATE_THRESHOLD_STR;
                                                            const splitTime = halfDayShift?.half_day_split_time?.slice(0, 5) || DEFAULT_HALF_DAY_SPLIT_STR;
                                                            setModalData(prev => ({ ...prev, newStatus: 'HALF_DAY', half_day_timing: 'Morning' }));
                                                            setEditCheckIn(shiftStart);
                                                            setEditCheckOut(splitTime);
                                                        } else {
                                                            setModalData(prev => ({ ...prev, newStatus: 'ON_LEAVE' }));
                                                            setEditCheckIn('');
                                                            setEditCheckOut('');
                                                        }
                                                    }}
                                                >
                                                    <option value="ON_LEAVE">Full Day</option>
                                                    <option value="HALF_DAY">Half Day</option>
                                                </select>
                                            </div>

                                            {modalData.newStatus === 'HALF_DAY' && (() => {
                                                // Dynamic shift timings — fallback to defaults if no shift assigned
                                                const shiftStart = halfDayShift?.start_time?.slice(0, 5) || DEFAULT_LATE_THRESHOLD_STR;
                                                const splitTime = halfDayShift?.half_day_split_time?.slice(0, 5) || DEFAULT_HALF_DAY_SPLIT_STR;
                                                const shiftEnd = halfDayShift?.end_time?.slice(0, 5) || DEFAULT_SHIFT_END_STR;
                                                const fmt12 = (t) => {
                                                    const [h, m] = t.split(':').map(Number);
                                                    const ampm = h >= 12 ? 'PM' : 'AM';
                                                    const h12 = h % 12 || 12;
                                                    return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
                                                };
                                                return (
                                                    <div className="space-y-3">
                                                        <div className="flex items-center gap-2">
                                                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1">Select Timing</label>
                                                            {!halfDayShift && <span className="text-[9px] text-blue-500 font-bold animate-pulse">Loading shift...</span>}
                                                            {halfDayShift && <span className="text-[9px] text-emerald-600 font-bold bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">{halfDayShift.shift_name}</span>}
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <button
                                                                onClick={() => {
                                                                    setModalData(prev => ({ ...prev, half_day_timing: 'Morning' }));
                                                                    setEditCheckIn(shiftStart);
                                                                    setEditCheckOut(splitTime);
                                                                }}
                                                                className={`py-3 px-4 rounded-xl text-xs font-black transition-all border-2 ${modalData.half_day_timing === 'Morning'
                                                                    ? 'bg-orange-500 border-orange-500 text-white shadow-lg shadow-orange-100'
                                                                    : 'bg-white border-gray-100 text-gray-500 hover:border-orange-200'}`}
                                                            >
                                                                Forenoon ({fmt12(shiftStart)} – {fmt12(splitTime)})
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setModalData(prev => ({ ...prev, half_day_timing: 'Afternoon' }));
                                                                    setEditCheckIn(splitTime);
                                                                    setEditCheckOut(shiftEnd);
                                                                }}
                                                                className={`py-3 px-4 rounded-xl text-xs font-black transition-all border-2 ${modalData.half_day_timing === 'Afternoon'
                                                                    ? 'bg-orange-500 border-orange-500 text-white shadow-lg shadow-orange-100'
                                                                    : 'bg-white border-gray-100 text-gray-500 hover:border-orange-200'}`}
                                                            >
                                                                Afternoon ({fmt12(splitTime)} – {fmt12(shiftEnd)})
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}

                                    {modalData?.newStatus === 'PERMISSION' && (() => {
                                        // Use shift start as the permission start shortcut base
                                        const permStart = halfDayShift?.start_time?.slice(0, 5) || DEFAULT_LATE_THRESHOLD_STR;
                                        const addHour = (t, hrs) => {
                                            const [h, m] = t.split(':').map(Number);
                                            const total = h * 60 + m + hrs * 60;
                                            return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
                                        };
                                        const permEnd1 = addHour(permStart, 1);
                                        const permEnd2 = addHour(permStart, 2);
                                        return (
                                            <div className="bg-purple-50 p-4 rounded-lg border border-purple-100 space-y-3">
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Select Duration</label>
                                                    <select
                                                        className="w-full border-gray-300 p-2 rounded-lg text-sm shadow-sm"
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            if (val === '1hr') {
                                                                setModalData(prev => ({ ...prev, permission_start_time: permStart, permission_end_time: permEnd1 }));
                                                            } else if (val === '2hr') {
                                                                setModalData(prev => ({ ...prev, permission_start_time: permStart, permission_end_time: permEnd2 }));
                                                            }
                                                        }}
                                                    >
                                                        <option value="">Custom...</option>
                                                        <option value="1hr">1 hr ({permStart} – {permEnd1})</option>
                                                        <option value="2hr">2 hrs ({permStart} – {permEnd2})</option>
                                                    </select>
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-400 mb-1 uppercase">From Time</label>
                                                        <input type="time" className="w-full border-gray-200 p-2 rounded-lg text-sm"
                                                            value={modalData.permission_start_time || ''}
                                                            onChange={e => setModalData({ ...modalData, permission_start_time: e.target.value })} />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-400 mb-1 uppercase">To Time</label>
                                                        <input type="time" className="w-full border-gray-200 p-2 rounded-lg text-sm"
                                                            value={modalData.permission_end_time || ''}
                                                            onChange={e => setModalData({ ...modalData, permission_end_time: e.target.value })} />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Reason / Note <span className="text-red-500">*</span></label>
                                        <textarea
                                            className="w-full border-gray-300 rounded-lg text-sm focus:ring-red-500 focus:border-red-500"
                                            rows="3"
                                            placeholder="Enter reason..."
                                            value={manualReason}
                                            onChange={e => setManualReason(e.target.value)}
                                        ></textarea>
                                    </div>
                                </div>
                            )}

                            {/* EDIT_PUNCH MODE CONTENT */}
                            {modalMode === 'EDIT_PUNCH' && (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4 bg-blue-50 p-3 rounded-lg border border-blue-100">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1">Check In Time</label>
                                            <input
                                                type="time"
                                                className="w-full border-gray-300 p-2 rounded-lg text-sm"
                                                value={editCheckIn}
                                                onChange={e => setEditCheckIn(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1">Check Out Time</label>
                                            <input
                                                type="time"
                                                className="w-full border-gray-300 p-2 rounded-lg text-sm"
                                                value={editCheckOut}
                                                onChange={e => setEditCheckOut(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Reason / Note <span className="text-red-500">*</span></label>
                                        <textarea
                                            className="w-full border-gray-300 rounded-lg text-sm focus:ring-red-500 focus:border-red-500"
                                            rows="2"
                                            placeholder="Reason for manual punch update..."
                                            value={manualReason}
                                            onChange={e => setManualReason(e.target.value)}
                                        ></textarea>
                                    </div>
                                </div>
                            )}

                            {/* Footer Buttons */}
                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => setModalOpen(false)}
                                    className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50"
                                >
                                    {modalMode === 'VIEW' ? 'Close' : 'Cancel'}
                                </button>
                                {(modalMode === 'EDIT' || modalMode === 'EDIT_PUNCH') && (
                                    <button
                                        onClick={submitManualUpdate}
                                        className={`flex-1 py-2 rounded-lg font-bold text-sm text-white shadow-md transition-all 
                                                ${(modalData?.newStatus === 'ON_LEAVE' || modalData?.newStatus === 'HALF_DAY') ? 'bg-orange-600 hover:bg-orange-700 shadow-orange-100' :
                                                modalData?.newStatus === 'ON_DUTY' ? 'bg-[#C30010] hover:bg-red-800 shadow-red-100' :
                                                    modalData?.newStatus === 'PRESENT' ? 'bg-green-600 hover:bg-green-700 shadow-green-100' :
                                                        modalData?.newStatus === 'LATE' ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-100' :
                                                            modalData?.newStatus === 'ABSENT' ? 'bg-red-600 hover:bg-red-700' :
                                                                'bg-purple-600 hover:bg-purple-700'} shadow-lg`}
                                    >
                                        Confirm
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Accrued Leave Update Modal */}
            {accruedModal.open && (
                <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in border border-gray-100">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-[#C30010] to-[#99000a] px-6 py-4 flex justify-between items-center text-white">
                            <div className="flex items-center gap-2">
                                <FiCalendar size={18} />
                                <h3 className="font-black text-sm uppercase tracking-wider">Update Accrued Leave Balance</h3>
                            </div>
                            <button
                                onClick={() => setAccruedModal({ open: false, user_id: null, name: '', designation: '', img: '', value: '', remarks: '', loading: false, error: null })}
                                className="text-white/80 hover:text-white transition-colors cursor-pointer"
                            >
                                <FiXCircle size={22} />
                            </button>
                        </div>

                        {/* Modal Form */}
                        <form onSubmit={handleSaveAccruedLeave} className="p-6 space-y-5">
                            {/* Employee Card Info */}
                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                                {accruedModal.img ? (
                                    <img src={accruedModal.img} alt="" className="w-11 h-11 rounded-xl object-cover border-2 border-white shadow" />
                                ) : (
                                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-gray-800 to-gray-600 text-white flex items-center justify-center font-black text-sm shadow">
                                        {accruedModal.name?.charAt(0) || 'E'}
                                    </div>
                                )}
                                <div>
                                    <p className="font-black text-gray-800 text-sm">{accruedModal.name}</p>
                                    <p className="text-[10px] text-red-600 font-black uppercase tracking-tight">{accruedModal.designation}</p>
                                </div>
                            </div>

                            {/* Error Banner */}
                            {accruedModal.error && (
                                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-xl text-xs font-bold">
                                    <FiAlertCircle size={16} className="shrink-0" />
                                    <span>{accruedModal.error}</span>
                                </div>
                            )}

                            {/* Accrued Leave Input */}
                            <div>
                                <label className="block text-xs font-black text-gray-600 uppercase tracking-wider mb-1.5">
                                    New Accrued Leave Balance <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    value={accruedModal.value}
                                    onChange={(e) => setAccruedModal(prev => ({ ...prev, value: e.target.value }))}
                                    placeholder="e.g. 8.0"
                                    required
                                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-bold text-gray-800 focus:ring-2 focus:ring-[#C30010] focus:border-[#C30010] outline-none shadow-sm transition-all"
                                />
                                <p className="text-[10px] text-gray-400 font-bold mt-1">
                                    Updating this sets the master baseline. The available balance will automatically recalculate.
                                </p>
                            </div>

                            {/* Remarks Input */}
                            <div>
                                <label className="block text-xs font-black text-gray-600 uppercase tracking-wider mb-1.5">
                                    Remarks / Reason <span className="text-gray-400 font-normal">(Optional)</span>
                                </label>
                                <textarea
                                    rows="2"
                                    value={accruedModal.remarks}
                                    onChange={(e) => setAccruedModal(prev => ({ ...prev, remarks: e.target.value }))}
                                    placeholder="e.g. Adjusted per HR audit"
                                    className="w-full border border-gray-200 rounded-xl px-4 py-2 text-xs font-medium text-gray-800 focus:ring-2 focus:ring-[#C30010] focus:border-[#C30010] outline-none shadow-sm transition-all resize-none"
                                />
                            </div>

                            {/* Modal Footer Buttons */}
                            <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
                                <button
                                    type="button"
                                    disabled={accruedModal.loading}
                                    onClick={() => setAccruedModal({ open: false, user_id: null, name: '', designation: '', img: '', value: '', remarks: '', loading: false, error: null })}
                                    className="px-4 py-2 bg-gray-100 text-gray-600 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-gray-200 transition-all cursor-pointer"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={accruedModal.loading}
                                    className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-[#C30010] to-[#99000a] text-white rounded-xl text-xs font-black uppercase tracking-wider hover:opacity-95 transition-all shadow-md disabled:opacity-50 cursor-pointer"
                                >
                                    {accruedModal.loading ? (
                                        <>
                                            <FiRefreshCw className="animate-spin" size={14} />
                                            <span>Updating...</span>
                                        </>
                                    ) : (
                                        <>
                                            <FiCheckCircle size={14} />
                                            <span>Update Accrued Leave</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal for Cancel Leave */}
            {cancelModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in border border-gray-100">
                        {/* Header */}
                        <div className="bg-[#C30010] px-6 py-4 flex justify-between items-center text-white">
                            <h3 className="font-extrabold text-sm uppercase tracking-wider">Cancel Leave / Permission</h3>
                            <button
                                onClick={() => setCancelModal({ open: false, row: null, reason: '' })}
                                className="p-1 hover:bg-white/10 rounded-full transition-all"
                            >
                                <FiXCircle size={20} />
                            </button>
                        </div>
                        {/* Body */}
                        <div className="p-6 space-y-4">
                            <div className="text-xs text-gray-500 font-bold leading-relaxed">
                                Are you sure you want to cancel the leave/permission for{' '}
                                <span className="text-gray-800 font-extrabold">{cancelModal.row?.name || 'this employee'}</span> on{' '}
                                <span className="text-gray-800 font-extrabold">{cancelModal.row?.attendance_date || cancelModal.row?.date}</span>?
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1.5">
                                    Cancellation Reason <span className="text-[#C30010]">*</span>
                                </label>
                                <textarea
                                    value={cancelModal.reason}
                                    onChange={(e) => setCancelModal(prev => ({ ...prev, reason: e.target.value }))}
                                    placeholder="Enter reason for cancelling the leave..."
                                    rows={3}
                                    className="w-full px-3 py-2 bg-gray-50 border border-gray-250 rounded-xl text-xs font-bold outline-none focus:border-[#C30010] focus:bg-white transition-all shadow-sm resize-none"
                                />
                            </div>
                        </div>
                        {/* Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
                            <button
                                onClick={() => setCancelModal({ open: false, row: null, reason: '' })}
                                className="px-4 py-2 bg-white hover:bg-gray-100 border border-gray-200 text-gray-600 rounded-xl text-xs font-bold transition-all"
                            >
                                Close
                            </button>
                            <button
                                onClick={submitCancelStatus}
                                className="px-4 py-2 bg-[#C30010] hover:bg-[#a1000d] text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm shadow-red-100"
                            >
                                Confirm Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const ShiftManagementView = ({
    shifts,
    selectedEmployeeForShift,
    employeeShiftsHistory,
    allEmployees,
    showShiftForm,
    editingShift,
    assigningShiftToEmployee,
    shiftFormDate,
    shiftFormEndDate,
    shiftFormReason,
    selectedShiftIdToAssign,
    shiftsLoading,
    editingEmployeeShift,
    isHROrAdmin,
    handleSaveShift,
    handleAssignShift,
    handleDeleteShift,
    handleDeleteEmployeeShift,
    handleSetDefaultShift,
    fetchEmployeeShiftsHistory,
    setSelectedEmployeeForShift,
    setSelectedShiftIdToAssign,
    setShiftFormDate,
    setShiftFormEndDate,
    setShiftFormReason,
    setAssigningShiftToEmployee,
    setEditingEmployeeShift,
    setShowShiftForm,
    setEditingShift,
    setDefaultShiftTarget,
    setDefaultShiftEffectiveDate,
    defaultShiftEffectiveDate,
    handleUpdateEmployeeShift,
    setShifts,
    setIsAssigningBulk,
    setSelectedEmployeesForBulk
}) => {
    const [empSearch, setEmpSearch] = useState('');

    const filteredEmployees = allEmployees.filter(emp => {
        if (Number(emp.account_status) !== 1) return false;
        const fullName = `${emp.first_name || ''} ${emp.last_name || ''}`.toLowerCase();
        const empId = (emp.employee_id || '').toLowerCase();
        return fullName.includes(empSearch.toLowerCase()) || empId.includes(empSearch.toLowerCase());
    });

    return (
        <div className="space-y-8 animate-fade-in-up mt-6">
            {/* Upper Section: Shift Configurations */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-150">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800">Shift Configurations</h3>
                        <p className="text-xs text-gray-500 mt-1">Manage corporate shift timings and thresholds.</p>
                    </div>
                    <button
                        onClick={() => {
                            setEditingShift(null);
                            setShowShiftForm(!showShiftForm);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#C30010] to-[#99000a] text-white rounded-xl text-xs font-black uppercase tracking-wider hover:opacity-95 shadow-sm transition-all"
                    >
                        <FiCalendar /> {showShiftForm ? 'Close Form' : 'Create New Shift'}
                    </button>
                </div>
                {showShiftForm && (
                    <motion.form
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        onSubmit={handleSaveShift}
                        className="bg-gray-50 p-6 rounded-xl border border-gray-150 mb-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 items-end"
                    >
                        <div className="sm:col-span-2">
                            <label className="block text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1">Shift Name</label>
                            <input
                                required
                                name="shift_name"
                                type="text"
                                defaultValue={editingShift?.shift_name || ''}
                                placeholder="e.g. Morning Shift"
                                className="w-full px-3 py-2 bg-white rounded-lg border border-gray-250 text-sm focus:outline-none focus:border-[#C30010] font-medium"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1">Start Time</label>
                            <input
                                required
                                name="start_time"
                                type="time"
                                step="1"
                                defaultValue={editingShift?.start_time || DEFAULT_SHIFT_START_STR + ':00'}
                                style={{ minWidth: '150px' }}
                                className="w-full px-3 py-2 bg-white rounded-lg border border-gray-250 text-sm focus:outline-none focus:border-[#C30010] font-medium font-mono"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1">End Time</label>
                            <input
                                required
                                name="end_time"
                                type="time"
                                step="1"
                                defaultValue={editingShift?.end_time || '18:00:00'}
                                style={{ minWidth: '150px' }}
                                className="w-full px-3 py-2 bg-white rounded-lg border border-gray-250 text-sm focus:outline-none focus:border-[#C30010] font-medium font-mono"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1">Late Threshold</label>
                            <input
                                required
                                name="late_threshold"
                                type="time"
                                step="1"
                                defaultValue={editingShift?.late_threshold || DEFAULT_LATE_THRESHOLD_FULL}
                                style={{ minWidth: '150px' }}
                                className="w-full px-3 py-2 bg-white rounded-lg border border-gray-250 text-sm focus:outline-none focus:border-[#C30010] font-medium font-mono"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1">Perm Threshold</label>
                            <input
                                required
                                name="permission_threshold"
                                type="time"
                                step="1"
                                defaultValue={editingShift?.permission_threshold || '09:15:00'}
                                style={{ minWidth: '150px' }}
                                className="w-full px-3 py-2 bg-white rounded-lg border border-gray-250 text-sm focus:outline-none focus:border-[#C30010] font-medium font-mono"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1">Half-Day Split</label>
                            <input
                                required
                                name="half_day_split_time"
                                type="time"
                                step="1"
                                defaultValue={editingShift?.half_day_split_time || DEFAULT_HALF_DAY_SPLIT_FULL}
                                style={{ minWidth: '150px' }}
                                className="w-full px-3 py-2 bg-white rounded-lg border border-gray-250 text-sm focus:outline-none focus:border-[#C30010] font-medium font-mono"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1">Went Late Grace</label>
                            <input
                                required
                                name="went_late_grace_time"
                                type="time"
                                step="1"
                                defaultValue={editingShift?.went_late_grace_time || '11:00:00'}
                                style={{ minWidth: '150px' }}
                                className="w-full px-3 py-2 bg-white rounded-lg border border-gray-250 text-sm focus:outline-none focus:border-[#C30010] font-medium font-mono"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1">Grace Period (Lates/Mo)</label>
                            <input
                                name="grace_period"
                                type="number"
                                defaultValue={editingShift?.grace_period ?? 0}
                                placeholder="0"
                                className="w-full px-3 py-2 bg-white rounded-lg border border-gray-250 text-sm focus:outline-none focus:border-[#C30010] font-medium"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1">Perm Limit (Hrs/Mo)</label>
                            <input
                                required
                                name="monthly_permission_limit"
                                type="number"
                                defaultValue={editingShift?.monthly_permission_limit ?? 3}
                                className="w-full px-3 py-2 bg-white rounded-lg border border-gray-250 text-sm focus:outline-none focus:border-[#C30010] font-medium"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-wider text-gray-500 mb-1 text-center">Auto Perm</label>
                            <label className="flex items-center justify-center w-full h-[38px] cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="auto_permission"
                                    defaultChecked={editingShift ? (editingShift.auto_permission == 1) : true}
                                    className="sr-only peer"
                                />
                                <div className="relative w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-emerald-500 transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5"></div>
                            </label>
                        </div>
                        <button
                            type="submit"
                            className="w-full px-4 py-2 bg-[#C30010] text-white rounded-lg font-bold text-xs uppercase tracking-wide hover:bg-[#a0000c] shadow-sm transition-all h-[38px] flex items-center justify-center"
                        >
                            Save
                        </button>
                    </motion.form>
                )}

                {shiftsLoading ? (
                    <div className="flex flex-col items-center py-10">
                        <div className="w-8 h-8 border-4 border-[#C30010] border-t-transparent rounded-full animate-spin mb-2"></div>
                        <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">Loading Shifts...</span>
                    </div>
                ) : shifts.length === 0 ? (
                    <div className="py-10 text-center text-gray-400 font-bold uppercase tracking-wider text-xs">
                        No shift templates configured yet.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                        {shifts.map((s) => (
                            <div
                                key={s.id}
                                className="bg-white p-5 rounded-2xl border border-gray-150 shadow-sm relative group hover:border-[#C30010]/35 transition-all"
                            >
                                <div className="absolute top-4 right-4 flex gap-2">
                                    {s.is_default == 1 ? (
                                        <button
                                            onClick={() => {
                                                setDefaultShiftTarget({ id: s.id, shift_name: s.shift_name, is_current: true });
                                                setDefaultShiftEffectiveDate(s.default_effective_date || new Date().toISOString().split('T')[0]);
                                            }}
                                            title="Current Default Shift (Click to edit Effective Date)"
                                            className="p-1.5 bg-amber-50 text-amber-500 rounded-lg border border-amber-200 cursor-pointer"
                                        >
                                            <FiStar size={13} fill="currentColor" />
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => {
                                                setDefaultShiftTarget({ id: s.id, shift_name: s.shift_name, is_current: false });
                                                setDefaultShiftEffectiveDate(new Date().toISOString().split('T')[0]);
                                            }}
                                            title="Set as Default Shift"
                                            className="p-1.5 hover:bg-amber-50 text-amber-500 rounded-lg transition-colors border border-gray-200"
                                        >
                                            <FiStar size={13} />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => {
                                            setEditingShift(s);
                                            setShowShiftForm(true);
                                        }}
                                        title="Edit Shift"
                                        className="p-1.5 hover:bg-gray-100 text-gray-600 rounded-lg transition-colors border border-gray-200"
                                    >
                                        <FiEdit2 size={13} />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteShift(s.id)}
                                        title="Delete Shift"
                                        className="p-1.5 hover:bg-red-50 text-red-600 rounded-lg transition-colors border border-red-150"
                                    >
                                        <FiTrash2 size={13} />
                                    </button>
                                </div>
                                <div className="flex items-center gap-2">
                                    <h4 className="font-extrabold text-gray-800 text-sm">{s.shift_name}</h4>
                                    {s.is_default == 1 && (
                                        <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded font-black text-[9px] uppercase tracking-wider border border-emerald-100">
                                            Default
                                        </span>
                                    )}
                                </div>
                                <div className="mt-4 space-y-2">
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-gray-400 font-semibold uppercase tracking-wider text-[9px]">Shift Timings:</span>
                                        <span className="text-gray-700 font-black font-mono">{s.start_time} - {s.end_time}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-gray-400 font-semibold uppercase tracking-wider text-[9px]">Late Threshold:</span>
                                        <span className="text-red-600 font-black font-mono">&gt;= {s.late_threshold}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-gray-400 font-semibold uppercase tracking-wider text-[9px]">Permission Threshold:</span>
                                        <span className="text-red-600 font-black font-mono">&gt;= {s.permission_threshold || '09:15:00'}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-gray-400 font-semibold uppercase tracking-wider text-[9px]">Half-Day Split:</span>
                                        <span className="text-gray-700 font-black font-mono">{s.half_day_split_time || DEFAULT_HALF_DAY_SPLIT_FULL}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-gray-400 font-semibold uppercase tracking-wider text-[9px]">Grace Period (Monthly Limit):</span>
                                        <span className="text-gray-700 font-bold">{s.grace_period || 0} Times</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-gray-400 font-semibold uppercase tracking-wider text-[9px]">Perm Limit (Hours):</span>
                                        <span className="text-gray-700 font-bold">{s.monthly_permission_limit ?? 3} Hrs/Month</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-gray-400 font-semibold uppercase tracking-wider text-[9px]">Went Late Grace:</span>
                                        <span className="text-gray-700 font-black font-mono">{s.went_late_grace_time || '11:00:00'}</span>
                                    </div>
                                    {/* Auto-Permission Toggle */}
                                    <div className="flex justify-between items-center pt-2 mt-2 border-t border-gray-100">
                                        <div>
                                            <span className="text-gray-500 font-semibold uppercase tracking-wider text-[9px] block">Auto Permission</span>
                                            <span className="text-gray-400 text-[8px]">1-hr perm on late punch</span>
                                        </div>
                                        <button
                                            type="button"
                                            title={s.auto_permission == 1 ? 'Auto-Permission ON — click to disable' : 'Auto-Permission OFF — click to enable'}
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                const newVal = s.auto_permission == 1 ? 0 : 1;

                                                // Optimistic Update: instantly update the local state for fluid UI transition
                                                setShifts(prev => prev.map(item => item.id === s.id ? { ...item, auto_permission: newVal } : item));

                                                try {
                                                    const res = await api.post('/attendance/save-shift', {
                                                        id: s.id,
                                                        shift_name: s.shift_name,
                                                        start_time: s.start_time,
                                                        end_time: s.end_time,
                                                        late_threshold: s.late_threshold,
                                                        permission_threshold: s.permission_threshold,
                                                        monthly_permission_limit: s.monthly_permission_limit,
                                                        half_day_split_time: s.half_day_split_time,
                                                        grace_period: s.grace_period,
                                                        auto_permission: newVal,
                                                        went_late_grace_time: s.went_late_grace_time,
                                                        status: s.status
                                                    });

                                                    if (res.data && res.data.status) {
                                                        toast.success(`Auto-Permission ${newVal === 1 ? 'enabled' : 'disabled'} for ${s.shift_name}`);
                                                    } else {
                                                        throw new Error(res.data?.message || 'Failed to update');
                                                    }
                                                } catch (err) {
                                                    // Revert local state if the backend save fails
                                                    setShifts(prev => prev.map(item => item.id === s.id ? { ...item, auto_permission: s.auto_permission } : item));
                                                    toast.error('Failed to update auto-permission setting');
                                                }
                                            }}
                                            className={`relative inline-flex items-center h-5 w-10 rounded-full transition-colors duration-200 focus:outline-none ${s.auto_permission == 1 ? 'bg-emerald-500' : 'bg-gray-300'
                                                }`}
                                        >
                                            <span className={`inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 ${s.auto_permission == 1 ? 'translate-x-5' : 'translate-x-1'
                                                }`} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Lower Section: Employee Shift Log & Assignment */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Employee Directory Column */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col h-[550px]">
                    <div className="flex justify-between items-center mb-2 shrink-0">
                        <h3 className="text-base font-bold text-gray-800">Employee Directory</h3>
                        <button
                            onClick={() => {
                                setIsAssigningBulk(true);
                                setSelectedEmployeesForBulk([]);
                            }}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#C30010]/5 text-[#C30010] rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-[#C30010]/10 transition-all border border-[#C30010]/10 shadow-sm"
                        >
                            <FiUsers size={12} /> Bulk Assign
                        </button>
                    </div>
                    <div className="relative mb-4 shrink-0">
                        <FiSearch className="absolute left-3 top-3.5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search employees..."
                            value={empSearch}
                            onChange={(e) => setEmpSearch(e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:border-[#C30010] font-medium"
                        />
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-2 divide-y divide-gray-50">
                        {filteredEmployees.map((emp) => {
                            const active = selectedEmployeeForShift?.user_id === emp.user_id;
                            return (
                                <button
                                    key={emp.user_id}
                                    onClick={() => {
                                        setSelectedEmployeeForShift(emp);
                                        fetchEmployeeShiftsHistory(emp.user_id);
                                        setAssigningShiftToEmployee(false);
                                    }}
                                    className={`w-full flex items-center gap-3 p-3 text-left transition-all ${active ? 'bg-red-50/70 border-l-4 border-[#C30010] rounded-r-xl' : 'hover:bg-gray-50 rounded-xl'
                                        }`}
                                >
                                    <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center font-bold text-[#C30010] uppercase text-xs">
                                        {emp.first_name?.charAt(0) || 'E'}
                                    </div>
                                    <div>
                                        <h4 className="font-extrabold text-gray-800 text-xs">{emp.first_name} {emp.last_name}</h4>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Shift Assignments & Chronological Log */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 lg:col-span-2 flex flex-col h-[550px]">
                    {!selectedEmployeeForShift ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400">
                            <FiUsers size={48} className="text-gray-250 mb-3" />
                            <span className="text-xs font-black uppercase tracking-wider">Select an employee to manage shifts</span>
                        </div>
                    ) : (
                        <div className="flex flex-col h-full overflow-hidden">
                            <div className="flex justify-between items-start border-b border-gray-100 pb-4 shrink-0">
                                <div>
                                    <h3 className="text-base font-bold text-gray-800">
                                        {selectedEmployeeForShift.first_name} {selectedEmployeeForShift.last_name}
                                    </h3>
                                </div>
                                <button
                                    onClick={() => {
                                        setAssigningShiftToEmployee(!assigningShiftToEmployee);
                                        setEditingEmployeeShift(null);
                                        setShiftFormReason('New shift assignment');
                                        setShiftFormEndDate('');
                                        if (shifts.length > 0 && !selectedShiftIdToAssign) {
                                            setSelectedShiftIdToAssign(shifts[0].id);
                                        }
                                    }}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-[#C30010]/5 text-[#C30010] rounded-xl text-xs font-black uppercase tracking-wider hover:bg-[#C30010]/10 transition-all"
                                >
                                    <FiCalendar /> {assigningShiftToEmployee ? 'Cancel' : 'Assign New Shift'}
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto py-4 space-y-6">
                                {assigningShiftToEmployee && (
                                    <motion.form
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        onSubmit={editingEmployeeShift ? handleUpdateEmployeeShift : handleAssignShift}
                                        className="bg-red-50/30 p-5 rounded-2xl border border-red-100/50 space-y-4"
                                    >
                                        <h4 className="text-xs font-black text-gray-700 uppercase tracking-widest">
                                            {editingEmployeeShift ? 'Edit Shift Assignment' : 'Temporal Shift Assignment'}
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-wider text-gray-500 mb-1">Select Shift</label>
                                                <select
                                                    required
                                                    value={selectedShiftIdToAssign}
                                                    onChange={(e) => setSelectedShiftIdToAssign(e.target.value)}
                                                    className="w-full px-3 py-2 bg-white rounded-lg border border-gray-250 text-xs focus:outline-none focus:border-[#C30010] font-semibold"
                                                >
                                                    {shifts.map(s => (
                                                        <option key={s.id} value={s.id}>{s.shift_name} ({s.start_time})</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-wider text-gray-500 mb-1">Effective Date</label>
                                                <div className="flex items-center bg-white rounded-lg p-0.5 border border-gray-250">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const d = new Date(shiftFormDate); d.setDate(d.getDate() - 1);
                                                            setShiftFormDate(d.toISOString().split('T')[0]);
                                                        }}
                                                        className="p-1 hover:bg-gray-100 rounded text-gray-600"
                                                    >
                                                        <FiChevronLeft size={14} />
                                                    </button>
                                                    <input
                                                        required
                                                        type="date"
                                                        value={shiftFormDate}
                                                        onChange={(e) => setShiftFormDate(e.target.value)}
                                                        className="bg-transparent border-none text-xs font-semibold text-gray-700 focus:ring-0 p-1 w-full text-center font-mono"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const d = new Date(shiftFormDate); d.setDate(d.getDate() + 1);
                                                            setShiftFormDate(d.toISOString().split('T')[0]);
                                                        }}
                                                        className="p-1 hover:bg-gray-100 rounded text-gray-600"
                                                    >
                                                        <FiChevronRight size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-wider text-gray-500 mb-1">End Date (Optional)</label>
                                                <div className="flex items-center bg-white rounded-lg p-0.5 border border-gray-250">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const currentVal = shiftFormEndDate || shiftFormDate || new Date().toISOString().split('T')[0];
                                                            const d = new Date(currentVal); d.setDate(d.getDate() - 1);
                                                            setShiftFormEndDate(d.toISOString().split('T')[0]);
                                                        }}
                                                        className="p-1 hover:bg-gray-100 rounded text-gray-600"
                                                    >
                                                        <FiChevronLeft size={14} />
                                                    </button>
                                                    <input
                                                        type="date"
                                                        value={shiftFormEndDate}
                                                        onChange={(e) => setShiftFormEndDate(e.target.value)}
                                                        className="bg-transparent border-none text-xs font-semibold text-gray-700 focus:ring-0 p-1 w-full text-center font-mono"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const currentVal = shiftFormEndDate || shiftFormDate || new Date().toISOString().split('T')[0];
                                                            const d = new Date(currentVal); d.setDate(d.getDate() + 1);
                                                            setShiftFormEndDate(d.toISOString().split('T')[0]);
                                                        }}
                                                        className="p-1 hover:bg-gray-100 rounded text-gray-600"
                                                    >
                                                        <FiChevronRight size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[9px] font-black uppercase tracking-wider text-gray-500 mb-1">Reason</label>
                                                <input
                                                    name="reason"
                                                    type="text"
                                                    value={shiftFormReason}
                                                    onChange={(e) => setShiftFormReason(e.target.value)}
                                                    placeholder="e.g. Special roster change"
                                                    className="w-full px-3 py-2 bg-white rounded-lg border border-gray-250 text-xs focus:outline-none focus:border-[#C30010] font-semibold"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex justify-end gap-2 mt-2">
                                            <button
                                                type="submit"
                                                className="px-4 py-2 bg-[#C30010] text-white rounded-lg font-black text-xs uppercase tracking-wider hover:bg-[#a0000c] shadow-sm transition-all"
                                            >
                                                {editingEmployeeShift ? 'Update Assignment' : 'Confirm Assignment'}
                                            </button>
                                        </div>
                                    </motion.form>
                                )}

                                {/* Timeline Shift History Logs */}
                                <div className="space-y-4">
                                    <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                        <RiFileHistoryFill className="text-gray-400" /> Shift History Timeline
                                    </h4>

                                    {employeeShiftsHistory.length === 0 ? (
                                        <div className="py-8 bg-gray-50 rounded-2xl text-center text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                                            Uses fallback default Shift (General Shift). Assign a new shift to log timeline history.
                                        </div>
                                    ) : (
                                        <div className="relative border-l-2 border-red-100 pl-4 ml-2 space-y-6">
                                            {employeeShiftsHistory.map((history, idx) => {
                                                const todayStr = new Date().toISOString().split('T')[0];
                                                const isUpcoming = history.effective_date > todayStr;
                                                const isActive = !isUpcoming && (!history.end_date || history.end_date >= todayStr);
                                                const isClosed = !isUpcoming && !isActive;
                                                return (
                                                    <div key={history.id} className="relative">
                                                        {/* Bullet point */}
                                                        <div className={`absolute -left-[23px] top-1.5 w-3 h-3 rounded-full border-2 ${isActive ? 'bg-[#C30010] border-red-200 animate-pulse'
                                                            : isUpcoming ? 'bg-blue-400 border-blue-100'
                                                                : 'bg-gray-300 border-gray-100'
                                                            }`} />

                                                        <div className="bg-white p-4 rounded-xl border border-gray-150 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                                                            <div>
                                                                <div className="flex items-center gap-2">
                                                                    <h5 className="font-extrabold text-gray-800 text-xs">{history.shift_name}</h5>
                                                                    {isActive ? (
                                                                        <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded font-bold text-[9px] uppercase tracking-wider border border-emerald-100">
                                                                            Active
                                                                        </span>
                                                                    ) : isUpcoming ? (
                                                                        <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-bold text-[9px] uppercase tracking-wider border border-blue-100">
                                                                            Upcoming
                                                                        </span>
                                                                    ) : (
                                                                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-bold text-[9px] uppercase tracking-wider">
                                                                            Closed
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <p className="text-[10px] text-gray-400 font-semibold mt-1 font-mono">
                                                                    Range: {formatDateDDMMYYYY(history.effective_date)} to {history.end_date ? formatDateDDMMYYYY(history.end_date) : 'Present'}
                                                                </p>
                                                                {history.reason && (
                                                                    <p className="text-[10px] text-gray-500 italic mt-1 font-medium flex items-center gap-1">
                                                                        <FiInfo size={11} className="text-gray-400 shrink-0" /> {history.reason}
                                                                    </p>
                                                                )}
                                                            </div>
                                                            <div className="flex flex-col items-end gap-2 shrink-0">
                                                                <div className="bg-gray-50 px-3 py-2 rounded-lg border border-gray-150 text-[10px] font-mono font-semibold text-gray-600 space-y-0.5">
                                                                    <div>Start: {history.start_time}</div>
                                                                    <div>End: {history.end_time}</div>
                                                                    <div>Late Threshold: {history.late_threshold}</div>
                                                                    <div>Half-Day Split: {history.half_day_split_time || DEFAULT_HALF_DAY_SPLIT_FULL}</div>
                                                                </div>
                                                                <div className="flex gap-2">
                                                                    <button
                                                                        onClick={() => {
                                                                            setEditingEmployeeShift(history);
                                                                            setSelectedShiftIdToAssign(history.shift_id);
                                                                            setShiftFormDate(history.effective_date);
                                                                            setShiftFormEndDate(history.end_date || '');
                                                                            setShiftFormReason(history.reason || 'New shift assignment');
                                                                            setAssigningShiftToEmployee(true);
                                                                        }}
                                                                        title="Edit Assignment"
                                                                        className="p-1.5 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-lg border border-gray-200 transition-colors"
                                                                    >
                                                                        <FiEdit2 size={12} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDeleteEmployeeShift(history.id)}
                                                                        title="Delete Assignment"
                                                                        className="p-1.5 bg-red-50 hover:bg-red-100 text-[#C30010] rounded-lg border border-red-100 transition-colors"
                                                                    >
                                                                        <FiTrash2 size={12} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AttendanceDashboard;

// Completed with fix for setDefaultShiftEffectiveDate ReferenceError.
